import {Component, inject, OnInit, ViewChild} from "@angular/core";
import {DatePipe} from "@angular/common";
import {loadProductCardTextOverrides, parseProductCardTextOverrides, validateProductCardTextOverrides} from '../../../models/product-card-texts.model';
import {
  ChatbotRegressionTestCase,
  DEFAULT_WIDGET_Z_INDEX,
  IAutoClose,
  IChatbotOptions,
  IFontFace,
  IImageCompositionFormValue,
  IImageCompositionOptions,
  ISampleMessage,
  IStyling,
  ITemplateMessage,
  MAX_WIDGET_Z_INDEX,
  MIN_WIDGET_Z_INDEX,
  createImageCompositionFormValue,
  normalizeWidgetZIndex,
  validateAndNormalizeImageCompositionOptions,
  validateCustomFontConfiguration
} from "../../../models/chatbot-options.model";
import {ChatbotVersionOptions, UpdateChatbotDraftRequest} from "../../../models/chatbot-version.model";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {ChatbotsService} from "../../../services/objects/chatbots.service";
import {ToastService} from "../../../services/layout/toast.service";
import {ConfirmDialogComponent} from "../../confirm-dialog/confirm-dialog.component";
import {MaterialModule} from "../../../material.module";
import {MatAccordion} from "@angular/material/expansion";
import {MatDialog} from "@angular/material/dialog";

import {ClientService} from "../../../services/auth/client.service";
import {WidgetSettingsComponent} from "./widget-settings/widget-settings.component";
import {createWidgetStylingForm} from './widget-settings/widget-styling-form';
import {WIDGET_STYLING_FIELDS} from '../../../models/widget-styling.model';
import {GeneralSettingsComponent} from "./general-settings/general-settings.component";
import {FrontendSettingsComponent} from "./frontend-settings/frontend-settings.component";
import {Marked} from "marked";
import {ListingsComponent} from "../../objects/listings/listings.component";
import {ChatbotReportComponent} from "../reports/chatbot-report.component";
import {AppRecentConversationsComponent} from "../../conversations/recent-conversation/recent-conversations.component";
import {TabbedPageComponent} from "../../objects/tabbed-page/tabbed-page.component";
import {ClientObjectSettings} from "../../objects/client-object-settings/client-object-settings.component";
import {DisabledButtonReasonComponent} from "../../shared/disabled-button-reason/disabled-button-reason.component";
import {environment} from '../../../../environments/environment';
import {
  cleanQuickStartText,
  quickStartTextRequiredValidator
} from './quick-start-editor/quick-start-message.utils';

type HubSpotSyncForm = {
  enabled: FormControl<boolean>;
  inboxId: FormControl<string>;
  handoverPolicy: FormControl<string>;
  pauseBotOnHandover: FormControl<boolean>;
};

const DEFAULT_WIDGET_SHADOW = '0px 16px 32px rgba(15, 23, 42, 0.22)';
const WIDGET_IMAGE_PATTERN = '^(https?://.+|data:image/(png|jpe?g|gif|webp|svg\\+xml);base64,(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{4}|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==))$';
const CSS_PADDING_PATTERN = '^(0|(?:\\d+(?:\\.\\d+)?(?:px|rem|em|%)))(\\s+(0|(?:\\d+(?:\\.\\d+)?(?:px|rem|em|%)))){0,3}$';
const PRODUCT_CARD_PRESENTATION_PATTERN = '^(horizontal|horizontal-compact|vertical|vertical-list|vertical-carousel|compact-carousel)$';
const SETTINGS_TAB_INDEX = 2;
const WIDGET_TAB_INDEX = 3;
const CHAT_WINDOW_TAB_INDEX = 4;

type PersistenceValidationIssue = {
  path: string;
  label: string;
  tabIndex: number;
};

const PERSISTENCE_VALIDATION_LABELS: Record<string, { label: string; tabIndex: number }> = {
  ...Object.fromEntries(WIDGET_STYLING_FIELDS.map(field => [
    `widgetForm.styling.${field.key}`, {label: field.label, tabIndex: WIDGET_TAB_INDEX}
  ])),
  'frontendForm.productCardSettings.productCardTextOverrides': { label: 'Product card texts', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'settingsForm.baseUrl': { label: 'Base URL', tabIndex: SETTINGS_TAB_INDEX },
  'frontendForm.generalSettings.logo': { label: 'Logo URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.generalSettings.zIndex': { label: 'Widget z-index', tabIndex: WIDGET_TAB_INDEX },
  'frontendForm.generalSettings.introMessage': { label: 'Intro text', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.generalSettings.firstMessage': { label: 'First message', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.generalSettings.inputPlaceholder': { label: 'Input field placeholder', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.generalSettings.sampleMessagesClickAnimationDelayMs': { label: 'Quick-start click animation delay', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.imageComposition.enabled': { label: 'Image composition activation', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.imageComposition.eligibleCategoriesText': { label: 'Image composition categories', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.imageComposition.disclaimerText': { label: 'Image composition disclaimer', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.productCardSettings.cardType': { label: 'Card Type', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.productCardSettings.cardLinkUrl': { label: 'Card Link URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.chatAvatar': { label: 'Chat Avatar URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.styling.chatBotWidth': { label: 'Chatbot Width', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.styling.fontFamily': { label: 'Font family', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.styling.fontFaceRegularUrl': { label: 'Regular font URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.styling.fontFaceMediumUrl': { label: 'Medium font URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.styling.fontFaceSemiBoldUrl': { label: 'Semi-bold font URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'frontendForm.styling.fontFaceBoldUrl': { label: 'Bold font URL', tabIndex: CHAT_WINDOW_TAB_INDEX },
  'widgetForm.images.aiLogo1': { label: 'Widget Logo 1 URL', tabIndex: WIDGET_TAB_INDEX },
  'widgetForm.images.aiLogo2': { label: 'Widget Logo 2 URL', tabIndex: WIDGET_TAB_INDEX },
  'widgetForm.images.launcherLogoPadding': { label: 'Launcher logo padding', tabIndex: WIDGET_TAB_INDEX },
  'widgetForm.colors.launcherBackground': { label: 'Launcher Background', tabIndex: WIDGET_TAB_INDEX }
};

function isCssColorValue(value: unknown): boolean {
  const trimmed = `${value ?? ''}`.trim();
  if (!trimmed) return true;
  return trimmed.toLowerCase() === 'transparent' || /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed);
}

const cssColorValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  isCssColorValue(control.value) ? null : { cssColor: true };

@Component({
  selector: 'app-chatbot-settings',
  templateUrl: './chatbot-settings.component.html',
  styleUrls: ['./chatbot-settings.component.scss'],
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    FormsModule,
    WidgetSettingsComponent,
    GeneralSettingsComponent,
    FrontendSettingsComponent,
    ListingsComponent,
    AppRecentConversationsComponent,
    ChatbotReportComponent,
    ClientObjectSettings,
    DisabledButtonReasonComponent,
    DatePipe
  ],
  providers: [Marked]
})
export class ChatbotSettingsComponent extends TabbedPageComponent implements OnInit{
    clientService = inject(ClientService);
    private fb = inject(FormBuilder);
    private chatbotsService = inject(ChatbotsService);
    private dialog = inject(MatDialog);
    private snackBar = inject(ToastService);
    private markedService = inject(Marked);


  // State Variables
  isSaving: boolean = false;
  isDeleting: boolean = false;
  isProposing: boolean = false;
  isLocalizing: boolean = false;
  isVersionAction = false;
  isSavingRegressionTests = false;
  refsHasChanged: boolean = false;

  summary: string;
  isSummaryLoading: boolean = false;

  // Chatbot Data
  chatbot: IChatbotOptions | null = null;
  id: string = '';

  assistants: string[] = [];

  // Reactive Forms
  settingsForm: FormGroup;
  widgetForm: FormGroup;
  frontendForm: FormGroup;
  messagesForm: FormGroup;
  localizationForm: FormGroup;
  localizationVariantsForm: FormArray;
  productsForm: FormGroup;
  hubspotForm: FormGroup<HubSpotSyncForm>;
  hubspotInboxes: Array<{ id: string; name: string }> = [];
  hubspotSyncError: string = '';
  private hubspotInboxesLoaded = false;

  @ViewChild(MatAccordion) accordion: MatAccordion;
  reportData: any[];
  chatbotVersions: ChatbotVersionOptions[] = [];
  activeDraft: ChatbotVersionOptions | null = null;
  regressionTestCases: ChatbotRegressionTestCase[] = [];
  validationTargetPath: string | null = null;
  readonly imageCompositionRuntimeReady = true;

  get chatUrl(): string | null {
    const clientId = this.clientService.client?.id;
    // Do not combine a newly selected client/route with the previously loaded chatbot.
    if (!clientId || !this.chatbot?.id || this.chatbot.clientId !== clientId || this.chatbot.id !== this.id) {
      return null;
    }

    try {
      const url = new URL(environment.frontendDummy);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
      url.search = '';
      url.hash = '';
      url.searchParams.set('client', clientId);
      url.searchParams.set('chatbot', this.chatbot.id);
      return url.toString();
    } catch {
      return null;
    }
  }

  get widgetZIndexControl(): FormControl<number | null> {
    return this.frontendForm.get('generalSettings.zIndex') as FormControl<number | null>;
  }

  ngOnInit(): void {
    super.OnInit();
    this.settingsForm = this.fb.group({
      baseUrl: ['', [Validators.required, Validators.pattern('https?://.+')]],
      conversationRetentionDays: [null, [Validators.min(1), Validators.max(3650)]],
      gptClassificationAndSanitizeQuery: [''],
      classificationModel: [''],
      gptLuceneQuery: [''],
      assistantInstructions: [''],
      aiProductCardsEnabled: [false],
      aiProductCardsInstruction: [''],
        usePromptCaching: [false],
      dynamicWidgetText: [false],
      dynamicWidgetTextInstruction: [''],
      chatSummaryPrompt: [''],
      proposeSampleMessagesPrompt: [''],
      cookiesToCollect: [[]]
    });

    // Widget form - rendered through widget-settings.component
    this.widgetForm = this.fb.group({
      text: [''],
      launcherShape: ['circle'],
      styling: createWidgetStylingForm(this.fb),
      popupTailEnabled: [true],
      hoverRevealEnabled: [true],
      positionMode: ['fixed'],
      mobilePopupOnce: [false],
      autoClose: this.fb.group({
        enabled: [true],
        delayMs: [2000, [Validators.min(0)]],
        devices: this.fb.group({
          desktop: [false],
          tablet: [false],
          mobile: [true]
        }),
        closeOnSameClientReferrer: [true]
      }),
      scrollReveal: this.fb.group({
        enabled: [false],
        thresholdPx: [800, [Validators.min(0)]],
        devices: this.fb.group({
          desktop: [true],
          tablet: [true],
          mobile: [true]
        })
      }),
      images: this.fb.group({
        aiLogo1: ['', Validators.pattern(WIDGET_IMAGE_PATTERN)],
        aiLogo2: ['', Validators.pattern(WIDGET_IMAGE_PATTERN)],
        logoSwapEnabled: [false],
        launcherLogoPadding: ['0px', Validators.pattern(CSS_PADDING_PATTERN)]
      }),
      colors: this.fb.group({
        backgroundColor: [''],
        textColor: [''],
        closeButtonBackground: [''],
        closeButtonColor: [''],
        closeButtonShadow: [''],
        popupShadow: [''],
        triangleShadow: [''],
        launcherBackground: ['', cssColorValidator],
        launcherShadow: ['']
      })
    });
    this.widgetForm.get('colors.launcherBackground')?.setValidators(cssColorValidator);
    this.widgetForm.get('colors.launcherBackground')?.updateValueAndValidity({ emitEvent: false });

    // Frontend form excluding message-related fields
    this.frontendForm = this.fb.group({
      generalSettings: this.fb.group({
        name: ['', ],
        logo: ['', Validators.pattern(WIDGET_IMAGE_PATTERN)],
        headerDisplayMode: [''],
        layoutPreset: [''],
        zIndex: [DEFAULT_WIDGET_Z_INDEX, [
          Validators.required,
          Validators.min(MIN_WIDGET_Z_INDEX),
          Validators.max(MAX_WIDGET_Z_INDEX),
          Validators.pattern(/^\d+$/)
        ]],
        logoWidth: [0],
        voiceEnabled: [false],
        attachmentsEnabled: [false],
        introMessage: ['', Validators.required],
        firstMessage: ['', Validators.required],
        inputPlaceholder: ['', Validators.required],
        submitButtonIcon: [''],
        resetButtonIcon: [''],
        attachButtonIcon: [''],
        voiceButtonIcon: [''],
        compactSubmitButtonIcon: [''],
        compactResetButtonIcon: [''],
        resetButtonTooltip: ['Click to reset your chat'],
        resetButtonConfirmationEnabled: [false],
        resetButtonConfirmationText: ['Click again to reset your chat'],
        sampleMessagesTitle: [''],
        sampleMessagesPresentation: [''],
        sampleMessagesHoverBorderColor: [''],
        sampleMessagesClickAnimationEnabled: [false],
        sampleMessagesClickAnimationDelayMs: [140, [Validators.min(0), Validators.max(600)]],
      }),
      imageComposition: this.createImageCompositionGroup(),
      productCardSettings: this.fb.group({
        productCardTextOverrides: [{}, validateProductCardTextOverrides],
        cardType: ['car', Validators.required],
        productCardPresentation: ['horizontal', Validators.pattern(PRODUCT_CARD_PRESENTATION_PATTERN)],
        productCardPresentationMode: ['fixed', Validators.pattern('^(fixed|dynamic)$')],
        productCardDynamicPresentations: [[]],
        addToCartEnabled: [false],
        productCardEcommerceTrackingEnabled: [false],
        ga4EcommerceEventsEnabled: [false],
        cardLinkUrl: ['', Validators.pattern('https?://.+')],
        cardUtmString: ['', Validators.pattern('^$|^(?:utm_[a-z_]+=[^&\\s]+)(?:&utm_[a-z_]+=[^&\\s]+)*$')],
        cardLocale: [''],
        cardCurrency: ['', Validators.pattern('^$|^[A-Z]{3}$')],
        productCardDetailsLabel: ['', Validators.maxLength(40)],
        productCardTestDriveLabel: ['', Validators.maxLength(40)]
      }),
      dropdownSettings: this.fb.group({
        dropdownEnabled: [false],
        dropdownBtnText: [''],
        dropdownBtnTextClose: [''],
        dropdownMessage: [''],
        dropdownBtnIconEnabled: [false]
      }),
      chatAvatar: ['', Validators.pattern(WIDGET_IMAGE_PATTERN)],
        chatAvatarEnabled: [true],
      styling: this.fb.group({
        advancedStylingEnabled: [true],
        chatBotWidth: ['1200', [Validators.pattern('^[0-9]+$')]],
        font: ['Inter+Tight'],
        fontProvider: ['google'],
        fontFamily: [''],
        fontFallback: ['Inter, Arial, sans-serif'],
        fontPreset: [''],
        fontFaceRegularUrl: [''],
        fontFaceMediumUrl: [''],
        fontFaceSemiBoldUrl: [''],
        fontFaceBoldUrl: [''],
        widgetText: [''],
          textLinkColor: [''],
        chatBubbleAssistant: [''],
          chatBubbleAssistantBorderRadiusDesktop: ['0 12px 12px 12px'],
          chatBubbleAssistantBorderRadiusMobile: ['12px 12px 12px 0'],
          chatBubbleAssistantFontSizeDesktop: ['16px'],
          chatBubbleAssistantFontSizeMobile: ['14px'],
        chatBubbleUser: [''],
          chatBubbleUserBorderRadius: ['12px 12px 0 12px'],
        avatarAccent: [''],
          chatAvatarSizeDesktop: ['50px'],
          chatAvatarSizeMobile: ['16px'],
        ctaPrimaryBackground: [''],
        ctaPrimaryHover: [''],
        ctaPrimaryText: [''],
        ctaPrimaryTextHover: [''],
        badgeBackground: [''],
        badgeText: [''],
        inputBackground: [''],
        inputPlaceholder: [''],
        inputBorderColor: ['#D0D5DD'],
        inputHoverBorderColor: ['#121413'],
        inputActiveBorderColor: [''],
        inputActiveSendBackground: ['#121413'],
        inputActiveSendColor: ['#FFFFFF'],
        inputFocus: [''],
        inputShadowEnabled: [false],
        dpBtnBgColor: [''],
        dpBtnBgColorHover: [''],
        dpBtnColor: [''],
        dpBtnColorHover: [''],
        recIconColor: [''],
        recIconColorHover: [''],
        resetButton: [''],
        resetButtonHoverBackground: ['#E31100'],
        resetButtonHoverColor: ['#FFFFFF'],
        resetButtonPressedBackground: ['#121413'],
        resetButtonPressedColor: ['#FFFFFF'],
        closeButton: [''],
        sugBgColor: [''],
        sugIconBgColor: [''],
        sugIconColor: [''],
        cardBtnBgColor: [''],
        cardBtnBgColorHover: [''],
        cardBtnColor: [''],
        cardBtnColorHover: [''],
        cardBtnBorderColor: [''],
        cardBtnBorderColorHover: [''],
        cardAddToCartBgColor: [''],
        cardAddToCartBgColorHover: [''],
        cardAddToCartColor: [''],
        cardAddToCartColorHover: [''],
        cardAddToCartBorderColor: [''],
        cardAddToCartBorderColorHover: [''],
        cardWidth: [''],
        cardMaxWidth: ['273px'],
        cardBorderColor: ['transparent'],
        cardBackgroundColor: ['transparent'],
        cardPadding: ['1rem 0 0'],
        cardTitleFontSize: ['19px'],
        cardTitleFontWeight: ['600'],
        cardDescriptionFontSize: ['15px'],
        cardDescriptionVisible: [true],
        cardCtaVisible: [true],
        cardPriceVisible: [true],
        cardPriceFontSize: ['17px'],
        cardPriceFontWeight: ['700'],
        cardPriceCurrentColor: ['#b42318'],
        cardPriceSaleCurrentColor: ['#b42318'],
        cardPriceOriginalColor: ['#111827'],
        cardPriceOriginalFontSize: ['15px'],
        cardImageAspectRatio: ['3:4'],
        cardCompactImageMaxHeight: [''],
        cardImageBackgroundColor: ['#f3f4f6'],
        cardBorderRadius: ['8px'],
        cardBadgeBackgroundColor: ['transparent', cssColorValidator],
        cardBadgeTextColor: ['#121413', cssColorValidator],
        cardSaleBadgeTextColor: ['#e31100', cssColorValidator],
        cardAttributeBackgroundColor: ['#f4f4f4', cssColorValidator],
        cardAttributeTextColor: ['#121413', cssColorValidator]
      }),
      css: ['', Validators.maxLength(50000)],
      js: ['']
    });

    this.messagesForm = this.fb.group({
      sampleMessages: this.fb.array([
        this.createSampleMessageGroup()
      ]),
      errorMessages: this.fb.array([
        this.createMessageGroup()
      ]),
      waitMessages: this.fb.array([
        this.createMessageGroup()
      ])
    });

    this.localizationForm = this.fb.group({
      defaultLocale: [''],
      supportedLocales: [[]],
      autoGenerate: [false]
    });
    this.localizationVariantsForm = this.fb.array([]);

    this.productsForm = this.fb.group({
      searchInstructions: [''],
      transformPatterns: this.fb.array([])
    });

    this.hubspotForm = this.fb.nonNullable.group(
      {
        enabled: this.fb.nonNullable.control(false),
        inboxId: this.fb.nonNullable.control(''),
        handoverPolicy: this.fb.nonNullable.control('auto'),
        pauseBotOnHandover: this.fb.nonNullable.control(true)
      },
      { validators: this.requireInboxWhenEnabled }
    );
    this.hubspotForm.controls.enabled.valueChanges.subscribe(enabled => {
      if (enabled) {
        this.loadHubSpotInboxes();
      } else {
        this.hubspotInboxes = [];
        this.hubspotSyncError = '';
        this.hubspotInboxesLoaded = false;
      }
    });
    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData();
    });
  }

  private createMessageGroup(): FormGroup {
    return this.fb.group({
      message: ['', Validators.required]
    });
  }

  private createImageCompositionGroup(initialValue?: Partial<IImageCompositionOptions>): FormGroup {
    const formValue = createImageCompositionFormValue(initialValue, this.imageCompositionRuntimeReady);
    return this.fb.group({
      enabled: [formValue.enabled],
      cameraCaptureEnabled: [formValue.cameraCaptureEnabled],
      eligibleCategoriesText: [formValue.eligibleCategoriesText],
      disclaimerText: [formValue.disclaimerText],
      preferredRequestTransport: [formValue.preferredRequestTransport],
      supportedRequestTransports: [formValue.supportedRequestTransports],
      uploadExpiryHours: [formValue.uploadExpiryHours],
      resultExpiryHours: [formValue.resultExpiryHours],
      ownership: this.fb.group({
        uploadBoundTo: [formValue.ownership.uploadBoundTo],
        resultBoundTo: [formValue.ownership.resultBoundTo]
      })
    });
  }

  private createSampleMessageGroup(message?: ISampleMessage): FormGroup {
    return this.fb.group({
      headline: [cleanQuickStartText(message?.headline), quickStartTextRequiredValidator],
      description: [cleanQuickStartText(message?.description), quickStartTextRequiredValidator],
      imageUrl: [message?.imageUrl || ''],
      imageAlt: [cleanQuickStartText(message?.imageAlt || message?.category)]
    });
  }

  onAssistantRefsChange(updatedRefs: any[]): void {
    this.chatbot!.assistantRefs = updatedRefs;
    this.refsHasChanged = true;
  }

  async loadData() {
    try {
      // Fetch Chatbot Data
      const chatbotResponse = await this.chatbotsService.getByIdAsync(this.id).toPromise();
      this.chatbot = chatbotResponse as IChatbotOptions;
      this.regressionTestCases = this.cloneRegressionTestCases(this.chatbot.regressionTestCases ?? []);

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date();

      this.chatbotsService.reportAsync(this.chatbot?.id ?? "", startDate.toISOString(), endDate.toISOString()).subscribe(data => {
        this.reportData = data;
      });

      // Populate Settings Form
      this.settingsForm.patchValue({
        baseUrl: this.chatbot?.baseUrl,
        conversationRetentionDays: this.chatbot?.conversationRetentionDays ?? null,
        gptClassificationAndSanitizeQuery: this.chatbot?.gptClassificationAndSanitizeQuery,
        classificationModel: (this.chatbot as any)?.classificationModel ?? '',
        gptLuceneQuery: this.chatbot?.gptLuceneQuery,
        assistantInstructions: (this.chatbot as any)?.assistantInstructions ?? '',
        aiProductCardsEnabled: (this.chatbot as any)?.aiProductCardsEnabled ?? false,
        aiProductCardsInstruction: (this.chatbot as any)?.aiProductCardsInstruction ?? '',
          usePromptCaching: this.chatbot?.usePromptCaching ?? false,
        dynamicWidgetText: (this.chatbot as any)?.dynamicWidgetText ?? false,
        dynamicWidgetTextInstruction: (this.chatbot as any)?.dynamicWidgetTextInstruction ?? '',
        chatSummaryPrompt: this.chatbot?.chatSummaryPrompt,
        proposeSampleMessagesPrompt: this.chatbot?.proposeSampleMessagesPrompt,
        cookiesToCollect: (this.chatbot?.cookiesToCollect ?? [])
      });

      // Populate HubSpot form
      const hubspotSync = (this.chatbot as any)?.hubSpotSync ?? {};
      this.hubspotForm.patchValue({
        enabled: !!hubspotSync.enabled,
        inboxId: hubspotSync.inboxId ?? '',
        handoverPolicy: hubspotSync.handoverPolicy ?? 'auto',
        pauseBotOnHandover: hubspotSync.pauseBotOnHandover ?? true
      });

      if (this.hubspotForm.controls.enabled.value) {
        this.loadHubSpotInboxes();
      }

      // Populate Widget Form
      this.widgetForm.patchValue({
        text: this.chatbot?.widgetOptions.text.aiMessage,
          launcherShape: this.chatbot?.widgetOptions.launcherShape ?? 'circle',
          styling: Object.fromEntries(WIDGET_STYLING_FIELDS.map(field => [
            field.key, this.chatbot?.widgetOptions.styling?.[field.key] ?? null
          ])),
          popupTailEnabled: this.chatbot?.widgetOptions.popupTailEnabled ?? true,
          hoverRevealEnabled: this.chatbot?.widgetOptions.hoverRevealEnabled ?? true,
          positionMode: this.chatbot?.widgetOptions.positionMode ?? 'fixed',
        mobilePopupOnce: this.chatbot?.widgetOptions.mobilePopupOnce ?? false,
        autoClose: {
          enabled: this.chatbot?.widgetOptions.autoClose?.enabled ?? true,
          delayMs: this.chatbot?.widgetOptions.autoClose?.delayMs ?? 2000,
          devices: {
            desktop: this.chatbot?.widgetOptions.autoClose?.devices?.desktop ?? false,
            tablet: this.chatbot?.widgetOptions.autoClose?.devices?.tablet ?? false,
            mobile: this.chatbot?.widgetOptions.autoClose?.devices?.mobile ?? true
          },
          closeOnSameClientReferrer: this.chatbot?.widgetOptions.autoClose?.closeOnSameClientReferrer ?? true
        },
        scrollReveal: {
          enabled: this.chatbot?.widgetOptions.scrollReveal?.enabled ?? false,
          thresholdPx: this.chatbot?.widgetOptions.scrollReveal?.thresholdPx ?? 800,
          devices: {
            desktop: this.chatbot?.widgetOptions.scrollReveal?.devices?.desktop ?? true,
            tablet: this.chatbot?.widgetOptions.scrollReveal?.devices?.tablet ?? true,
            mobile: this.chatbot?.widgetOptions.scrollReveal?.devices?.mobile ?? true
          }
        },
        images: {
          aiLogo1: this.chatbot?.widgetOptions.images.aiLogo1,
          aiLogo2: this.chatbot?.widgetOptions.images.aiLogo2,
          logoSwapEnabled: this.chatbot?.widgetOptions.images.logoSwapEnabled ?? false,
          launcherLogoPadding: this.chatbot?.widgetOptions.images.launcherLogoPadding ?? '0px'
        },
        colors: this.chatbot?.widgetOptions.colors
      });

      const widgetColors = this.chatbot?.widgetOptions.colors;
      if (widgetColors) {
        widgetColors.closeButtonShadow = widgetColors.closeButtonShadow || DEFAULT_WIDGET_SHADOW;
        widgetColors.popupShadow = widgetColors.popupShadow || DEFAULT_WIDGET_SHADOW;
        widgetColors.triangleShadow = widgetColors.triangleShadow || DEFAULT_WIDGET_SHADOW;
      }

      const localization = this.chatbot?.localization ?? {
        defaultLocale: '',
        supportedLocales: [],
        autoGenerate: false,
        variants: {}
      };
      this.localizationForm.patchValue({
        defaultLocale: localization.defaultLocale ?? '',
        supportedLocales: localization.supportedLocales ?? [],
        autoGenerate: localization.autoGenerate ?? false
      });
      this.localizationForm.markAsPristine();
      this.populateLocalizationVariants(localization.variants ?? {});

      // Populate Frontend Form
      this.frontendForm.patchValue({
        generalSettings: {
          name: this.chatbot?.frontEndOptions.name,
          logo: this.chatbot?.frontEndOptions.logo,
          headerDisplayMode: this.chatbot?.frontEndOptions.headerDisplayMode ?? '',
          layoutPreset: this.normalizeLayoutPreset(this.chatbot?.frontEndOptions.layoutPreset),
          zIndex: normalizeWidgetZIndex(this.chatbot?.frontEndOptions.zIndex),
          logoWidth: this.chatbot?.frontEndOptions.logoWidth,
          voiceEnabled: this.chatbot?.frontEndOptions.voiceEnabled,
          attachmentsEnabled: this.chatbot?.frontEndOptions.attachmentsEnabled ?? false,
          introMessage: this.chatbot?.frontEndOptions?.introMessage || '',
          firstMessage: this.chatbot?.frontEndOptions?.firstMessage || '',
          inputPlaceholder: this.chatbot?.frontEndOptions?.inputPlaceholder || '',
          submitButtonIcon: this.chatbot?.frontEndOptions?.submitButtonIcon || '',
          resetButtonIcon: this.chatbot?.frontEndOptions?.resetButtonIcon || '',
          attachButtonIcon: this.chatbot?.frontEndOptions?.attachButtonIcon || '',
          voiceButtonIcon: this.chatbot?.frontEndOptions?.voiceButtonIcon || '',
          compactSubmitButtonIcon: this.chatbot?.frontEndOptions?.compactSubmitButtonIcon || '',
          compactResetButtonIcon: this.chatbot?.frontEndOptions?.compactResetButtonIcon || '',
          resetButtonTooltip: this.chatbot?.frontEndOptions?.resetButtonTooltip || 'Click to reset your chat',
          resetButtonConfirmationEnabled: this.chatbot?.frontEndOptions?.resetButtonConfirmationEnabled ?? false,
          resetButtonConfirmationText: this.chatbot?.frontEndOptions?.resetButtonConfirmationText || 'Click again to reset your chat',
          sampleMessagesTitle: this.chatbot?.frontEndOptions?.sampleMessagesTitle || '',
          sampleMessagesPresentation: this.chatbot?.frontEndOptions?.sampleMessagesPresentation || '',
          sampleMessagesHoverBorderColor: this.chatbot?.frontEndOptions?.sampleMessagesHoverBorderColor || '',
          sampleMessagesClickAnimationEnabled: this.chatbot?.frontEndOptions?.sampleMessagesClickAnimationEnabled ?? false,
          sampleMessagesClickAnimationDelayMs: this.chatbot?.frontEndOptions?.sampleMessagesClickAnimationDelayMs ?? 140,

        },
        imageComposition: createImageCompositionFormValue(
          this.chatbot?.frontEndOptions?.imageComposition,
          this.imageCompositionRuntimeReady
        ),
        productCardSettings: {
          cardType: this.chatbot?.frontEndOptions.cardType,
          productCardPresentation: this.normalizeProductCardPresentation(this.chatbot?.frontEndOptions.productCardPresentation),
          productCardPresentationMode: this.normalizeProductCardPresentationMode(this.chatbot?.frontEndOptions.productCardPresentationMode),
          productCardDynamicPresentations: this.normalizeProductCardDynamicPresentations(this.chatbot?.frontEndOptions.productCardDynamicPresentations),
          addToCartEnabled: this.chatbot?.frontEndOptions.addToCartEnabled ?? false,
          productCardEcommerceTrackingEnabled: this.chatbot?.frontEndOptions.productCardEcommerceTrackingEnabled ?? false,
          ga4EcommerceEventsEnabled: this.chatbot?.frontEndOptions.ga4EcommerceEventsEnabled ?? false,
          cardLinkUrl: this.chatbot?.frontEndOptions.cardLinkUrl,
          cardUtmString: this.chatbot?.frontEndOptions.cardUtmString,
          cardLocale: this.chatbot?.frontEndOptions.cardLocale,
          cardCurrency: this.chatbot?.frontEndOptions.cardCurrency,
          productCardTextOverrides: loadProductCardTextOverrides(this.chatbot?.frontEndOptions, this.chatbot?.localization),
          productCardDetailsLabel: this.chatbot?.frontEndOptions.productCardDetailsLabel ?? '',
          productCardTestDriveLabel: this.chatbot?.frontEndOptions.productCardTestDriveLabel ?? ''
        },
        dropdownSettings: {
          dropdownEnabled: this.chatbot?.frontEndOptions.dropdownEnabled,
          dropdownBtnText: this.chatbot?.frontEndOptions.dropdownBtnText,
          dropdownBtnTextClose: this.chatbot?.frontEndOptions.dropdownBtnTextClose,
          dropdownMessage: this.chatbot?.frontEndOptions.dropdownMessage,
          dropdownBtnIconEnabled: this.chatbot?.frontEndOptions.dropdownBtnIconEnabled
        },
        chatAvatar: this.chatbot?.frontEndOptions.chatAvatar,
          chatAvatarEnabled: this.chatbot?.frontEndOptions.chatAvatarEnabled !== false,
          styling: {
          advancedStylingEnabled: this.chatbot?.frontEndOptions.styling.advancedStylingEnabled,
          chatBotWidth: this.chatbot?.frontEndOptions.styling.chatBotWidth ?? '1200',
          font: this.chatbot?.frontEndOptions.styling.font ?? 'Inter+Tight',
          fontProvider: this.resolveFontProvider(this.chatbot?.frontEndOptions.styling),
          fontFamily: this.chatbot?.frontEndOptions.styling.fontFamily ?? '',
          fontFallback: this.chatbot?.frontEndOptions.styling.fontFallback ?? 'Inter, Arial, sans-serif',
          fontPreset: this.chatbot?.frontEndOptions.styling.fontPreset ?? '',
          fontFaceRegularUrl: this.getFontFaceUrl(this.chatbot?.frontEndOptions.styling, 400),
          fontFaceMediumUrl: this.getFontFaceUrl(this.chatbot?.frontEndOptions.styling, 500),
          fontFaceSemiBoldUrl: this.getFontFaceUrl(this.chatbot?.frontEndOptions.styling, 600),
          fontFaceBoldUrl: this.getFontFaceUrl(this.chatbot?.frontEndOptions.styling, 700),
          widgetText: this.chatbot?.frontEndOptions.styling.widgetText,
          textLinkColor: this.chatbot?.frontEndOptions.styling.textLinkColor ?? '',
          chatBubbleAssistant: this.chatbot?.frontEndOptions.styling.chatBubbleAssistant,
          chatBubbleAssistantBorderRadiusDesktop: this.chatbot?.frontEndOptions.styling.chatBubbleAssistantBorderRadiusDesktop ?? '0 12px 12px 12px',
          chatBubbleAssistantBorderRadiusMobile: this.chatbot?.frontEndOptions.styling.chatBubbleAssistantBorderRadiusMobile ?? '12px 12px 12px 0',
          chatBubbleAssistantFontSizeDesktop: this.chatbot?.frontEndOptions.styling.chatBubbleAssistantFontSizeDesktop ?? '16px',
          chatBubbleAssistantFontSizeMobile: this.chatbot?.frontEndOptions.styling.chatBubbleAssistantFontSizeMobile ?? '14px',
          chatBubbleUser: this.chatbot?.frontEndOptions.styling.chatBubbleUser,
          chatBubbleUserBorderRadius: this.chatbot?.frontEndOptions.styling.chatBubbleUserBorderRadius ?? '12px 12px 0 12px',
          avatarAccent: this.chatbot?.frontEndOptions.styling.avatarAccent,
          chatAvatarSizeDesktop: this.chatbot?.frontEndOptions.styling.chatAvatarSizeDesktop ?? '50px',
          chatAvatarSizeMobile: this.chatbot?.frontEndOptions.styling.chatAvatarSizeMobile ?? '16px',
          ctaPrimaryBackground: this.chatbot?.frontEndOptions.styling.ctaPrimaryBackground,
          ctaPrimaryHover: this.chatbot?.frontEndOptions.styling.ctaPrimaryHover,
          ctaPrimaryText: this.chatbot?.frontEndOptions.styling.ctaPrimaryText,
          ctaPrimaryTextHover: this.chatbot?.frontEndOptions.styling.ctaPrimaryTextHover,
          badgeBackground: this.chatbot?.frontEndOptions.styling.badgeBackground,
          badgeText: this.chatbot?.frontEndOptions.styling.badgeText,
          inputBackground: this.chatbot?.frontEndOptions.styling.inputBackground,
          inputPlaceholder: this.chatbot?.frontEndOptions.styling.inputPlaceholder,
          inputBorderColor: this.chatbot?.frontEndOptions.styling.inputBorderColor ?? '#D0D5DD',
          inputHoverBorderColor: this.chatbot?.frontEndOptions.styling.inputHoverBorderColor ?? '#121413',
          inputActiveBorderColor: this.chatbot?.frontEndOptions.styling.inputActiveBorderColor ?? '',
          inputActiveSendBackground: this.chatbot?.frontEndOptions.styling.inputActiveSendBackground ?? '#121413',
          inputActiveSendColor: this.chatbot?.frontEndOptions.styling.inputActiveSendColor ?? '#FFFFFF',
          inputFocus: this.chatbot?.frontEndOptions.styling.inputFocus,
          inputShadowEnabled: this.chatbot?.frontEndOptions.styling.inputShadowEnabled ?? false,
          dpBtnBgColor: this.chatbot?.frontEndOptions.styling.dpBtnBgColor,
          dpBtnBgColorHover: this.chatbot?.frontEndOptions.styling.dpBtnBgColorHover,
          dpBtnColor: this.chatbot?.frontEndOptions.styling.dpBtnColor,
          dpBtnColorHover: this.chatbot?.frontEndOptions.styling.dpBtnColorHover,
          recIconColor: this.chatbot?.frontEndOptions.styling.recIconColor,
          recIconColorHover: this.chatbot?.frontEndOptions.styling.recIconColorHover,
          resetButton: this.chatbot?.frontEndOptions.styling.resetButton,
          resetButtonHoverBackground: this.chatbot?.frontEndOptions.styling.resetButtonHoverBackground ?? '#E31100',
          resetButtonHoverColor: this.chatbot?.frontEndOptions.styling.resetButtonHoverColor ?? '#FFFFFF',
          resetButtonPressedBackground: this.chatbot?.frontEndOptions.styling.resetButtonPressedBackground ?? '#121413',
          resetButtonPressedColor: this.chatbot?.frontEndOptions.styling.resetButtonPressedColor ?? '#FFFFFF',
          closeButton: this.chatbot?.frontEndOptions.styling.closeButton,
          sugBgColor: this.chatbot?.frontEndOptions.styling.sugBgColor,
          sugIconBgColor: this.chatbot?.frontEndOptions.styling.sugIconBgColor,
          sugIconColor: this.chatbot?.frontEndOptions.styling.sugIconColor,
          cardBtnBgColor: this.chatbot?.frontEndOptions.styling.cardBtnBgColor,
          cardBtnBgColorHover: this.chatbot?.frontEndOptions.styling.cardBtnBgColorHover,
          cardBtnColor: this.chatbot?.frontEndOptions.styling.cardBtnColor,
          cardBtnColorHover: this.chatbot?.frontEndOptions.styling.cardBtnColorHover,
          cardBtnBorderColor: this.chatbot?.frontEndOptions.styling.cardBtnBorderColor ?? '',
          cardBtnBorderColorHover: this.chatbot?.frontEndOptions.styling.cardBtnBorderColorHover ?? '',
          cardAddToCartBgColor: this.chatbot?.frontEndOptions.styling.cardAddToCartBgColor ?? '',
          cardAddToCartBgColorHover: this.chatbot?.frontEndOptions.styling.cardAddToCartBgColorHover ?? '',
          cardAddToCartColor: this.chatbot?.frontEndOptions.styling.cardAddToCartColor ?? '',
          cardAddToCartColorHover: this.chatbot?.frontEndOptions.styling.cardAddToCartColorHover ?? '',
          cardAddToCartBorderColor: this.chatbot?.frontEndOptions.styling.cardAddToCartBorderColor ?? '',
          cardAddToCartBorderColorHover: this.chatbot?.frontEndOptions.styling.cardAddToCartBorderColorHover ?? '',
          cardWidth: this.chatbot?.frontEndOptions.styling.cardWidth ?? '',
          cardMaxWidth: this.chatbot?.frontEndOptions.styling.cardMaxWidth ?? '273px',
          cardBorderColor: this.chatbot?.frontEndOptions.styling.cardBorderColor ?? 'transparent',
          cardBackgroundColor: this.chatbot?.frontEndOptions.styling.cardBackgroundColor ?? 'transparent',
          cardPadding: this.chatbot?.frontEndOptions.styling.cardPadding ?? '1rem 0 0',
          cardTitleFontSize: this.chatbot?.frontEndOptions.styling.cardTitleFontSize ?? '19px',
          cardTitleFontWeight: this.chatbot?.frontEndOptions.styling.cardTitleFontWeight ?? '600',
          cardDescriptionFontSize: this.chatbot?.frontEndOptions.styling.cardDescriptionFontSize ?? '15px',
          cardDescriptionVisible: this.chatbot?.frontEndOptions.styling.cardDescriptionVisible ?? true,
          cardCtaVisible: this.chatbot?.frontEndOptions.styling.cardCtaVisible ?? true,
          cardPriceVisible: this.chatbot?.frontEndOptions.styling.cardPriceVisible ?? true,
          cardPriceFontSize: this.chatbot?.frontEndOptions.styling.cardPriceFontSize ?? '17px',
          cardPriceFontWeight: this.chatbot?.frontEndOptions.styling.cardPriceFontWeight ?? '700',
          cardPriceCurrentColor: this.chatbot?.frontEndOptions.styling.cardPriceCurrentColor ?? '#b42318',
          cardPriceSaleCurrentColor: this.chatbot?.frontEndOptions.styling.cardPriceSaleCurrentColor ?? this.chatbot?.frontEndOptions.styling.cardPriceCurrentColor ?? '#b42318',
          cardPriceOriginalColor: this.chatbot?.frontEndOptions.styling.cardPriceOriginalColor ?? '#111827',
          cardPriceOriginalFontSize: this.chatbot?.frontEndOptions.styling.cardPriceOriginalFontSize ?? '15px',
          cardImageAspectRatio: this.chatbot?.frontEndOptions.styling.cardImageAspectRatio ?? '3:4',
          cardCompactImageMaxHeight: this.chatbot?.frontEndOptions.styling.cardCompactImageMaxHeight ?? '',
          cardImageBackgroundColor: this.chatbot?.frontEndOptions.styling.cardImageBackgroundColor ?? '#f3f4f6',
          cardBorderRadius: this.chatbot?.frontEndOptions.styling.cardBorderRadius ?? '8px',
          cardBadgeBackgroundColor: this.chatbot?.frontEndOptions.styling.cardBadgeBackgroundColor ?? 'transparent',
          cardBadgeTextColor: this.chatbot?.frontEndOptions.styling.cardBadgeTextColor ?? '#121413',
          cardSaleBadgeTextColor: this.chatbot?.frontEndOptions.styling.cardSaleBadgeTextColor ?? '#e31100',
          cardAttributeBackgroundColor: this.chatbot?.frontEndOptions.styling.cardAttributeBackgroundColor ?? '#f4f4f4',
          cardAttributeTextColor: this.chatbot?.frontEndOptions.styling.cardAttributeTextColor ?? '#121413'
        },
        css: this.chatbot?.frontEndOptions.css,
        js: this.chatbot?.frontEndOptions.js

      });
      this.frontendForm.get('styling')?.get('advancedStylingEnabled')?.setValue(true);
      // Clear and populate the sampleMessages array safely
      const sampleMessagesArray = this.messagesForm.get('sampleMessages') as FormArray;
      sampleMessagesArray.clear();

      if (Array.isArray(this.chatbot?.frontEndOptions?.sampleMessages)) {
        this.chatbot.frontEndOptions.sampleMessages.forEach(msg => {
          sampleMessagesArray.push(this.createSampleMessageGroup(msg));
        });
      }

      // Clear and populate the errorMessages array safely
      const errorMessagesArray = this.messagesForm.get('errorMessages') as FormArray;
      errorMessagesArray.clear();

      if (Array.isArray(this.chatbot?.frontEndOptions?.errorMessages)) {
        this.chatbot.frontEndOptions.errorMessages.forEach(msg => {
          errorMessagesArray.push(this.fb.group({ message: msg.message || '' }));
        });
      }


      // Fetch Assistants and Master Threads
      this.AddAssistants();
      await this.LoadSummary();
      await this.loadRegressionTests();
      await this.loadVersions();



      // Initialize Transformers and Other Forms
    } catch (error) {
      console.error('Error loading data', error);
      this.snackBar.showError('Error loading data', 3000);
    }
  }

  private async loadVersions(): Promise<void> {
    if (!this.chatbot?.id) return;
    try {
      const versions = await this.chatbotsService.getVersionsAsync(this.chatbot.id).toPromise();
      this.chatbotVersions = versions ?? [];
      this.activeDraft = this.chatbotVersions.find(version => version.status === 'draft') ?? null;
    } catch (error) {
      console.error('Error loading chatbot versions', error);
      this.chatbotVersions = [];
      this.activeDraft = null;
    }
  }

  private async loadRegressionTests(): Promise<void> {
    if (!this.chatbot?.id) return;
    try {
      const testCases = await this.chatbotsService.getRegressionTestsAsync(this.chatbot.id).toPromise();
      this.regressionTestCases = this.cloneRegressionTestCases(testCases ?? []);
      this.chatbot.regressionTestCases = this.cloneRegressionTestCases(this.regressionTestCases);
    } catch (error) {
      console.error('Error loading chatbot regression tests', error);
      this.regressionTestCases = this.cloneRegressionTestCases(this.chatbot.regressionTestCases ?? []);
    }
  }

  loadHubSpotInboxes(): void {
    if (!this.chatbot?.id) return;
    if (this.hubspotInboxesLoaded) return;
    this.hubspotInboxesLoaded = true;
    this.chatbotsService.getHubSpotInboxesAsync(this.chatbot.id).subscribe({
      next: (inboxes) => {
        this.hubspotInboxes = inboxes || [];
      },
      error: (err) => {
        console.error('Failed to load HubSpot inboxes', err);
      }
    });
  }

  private buildHubSpotSyncPayload() {
    return {
      enabled: !!this.hubspotForm.value.enabled,
      inboxId: this.hubspotForm.value.inboxId,
      handoverPolicy: this.hubspotForm.value.handoverPolicy,
      pauseBotOnHandover: this.hubspotForm.value.pauseBotOnHandover
    };
  }

  private requireInboxWhenEnabled(control: AbstractControl): ValidationErrors | null {
    const group = control as FormGroup<HubSpotSyncForm>;
    const enabled = group.controls.enabled.value;
    const inbox = (group.controls.inboxId.value || '').trim();
    if (!enabled) return null;
    return inbox.length > 0 ? null : { inboxRequired: true };
  }

  AddAssistants() {
    console.log('Saving chatbot', this.assistants);
    this.assistants = this.chatbot?.assistantRefs ?? [];
  }

  async LoadSummary() {
    if (!this.chatbot) return;

    try {
      this.isSummaryLoading = true;
      const resp = await this.chatbotsService.summarizeLastWeekAsync(this.chatbot.id).toPromise() ?? "";
      this.summary = this.markedService.parse(resp) as string;
    } catch (error) {
      console.error('Error loading summary', error);
      this.snackBar.showError('Error loading summary', 3000);
    } finally {
      this.isSummaryLoading = false;
    }
  }

  get sampleMessages(): FormArray {
    return this.messagesForm.get('sampleMessages') as FormArray;
  }

  get errorMessages(): FormArray {
    return this.messagesForm.get('errorMessages') as FormArray;
  }

  get waitMessages(): FormArray {
    return this.messagesForm.get('waitMessages') as FormArray;
  }

  get aiProductCardsEnabledControl(): FormControl<boolean> {
    return this.settingsForm.get('aiProductCardsEnabled') as FormControl<boolean>;
  }

  get aiProductCardsInstructionControl(): FormControl<string> {
    return this.settingsForm.get('aiProductCardsInstruction') as FormControl<string>;
  }

  get imageCompositionPersistenceBlocked(): boolean {
    return this.getImageCompositionValidation().errors.length > 0;
  }

  get settingsPersistenceInvalid(): boolean {
    return this.settingsForm.invalid ||
      this.imageCompositionPersistenceBlocked ||
      this.getQuickStartPersistenceIssue() !== null ||
      this.hasBlockingDirtyInvalidControl(this.frontendForm) ||
      this.hasBlockingDirtyInvalidControl(this.widgetForm);
  }

  get hasSaveableChanges(): boolean {
    return this.settingsForm.dirty ||
      this.messagesForm.dirty ||
      this.frontendForm.dirty ||
      this.widgetForm.dirty ||
      this.localizationForm.dirty ||
      this.localizationVariantsForm.dirty ||
      this.hubspotForm.dirty ||
      this.refsHasChanged;
  }

  get saveDisabled(): boolean {
    return this.isSaving ||
      this.isVersionAction ||
      this.imageCompositionPersistenceBlocked ||
      (this.hubspotForm.dirty && this.hubspotForm.invalid) ||
      !this.hasSaveableChanges;
  }

  get saveDisabledReason(): string {
    if (this.isSaving) return 'Settings are currently saving.';
    if (this.isVersionAction) return 'A draft or publishing action is currently running.';
    if (this.imageCompositionPersistenceBlocked) {
      return this.getImageCompositionValidation().errors[0];
    }
    if (this.hubspotForm.dirty && this.hubspotForm.invalid) {
      return 'Select a HubSpot inbox before saving HubSpot sync changes.';
    }
    if (!this.hasSaveableChanges) return 'Make a change before saving.';
    return '';
  }

  get saveDraftDisabled(): boolean {
    return this.saveDraftDisabledReason.length > 0;
  }

  get saveDraftDisabledReason(): string {
    const operationReason = this.blockingOperationReason;
    if (operationReason) return operationReason;
    if (this.imageCompositionPersistenceBlocked) return this.getImageCompositionValidation().errors[0];
    if (this.settingsPersistenceInvalid) return 'Fix invalid settings before saving a draft.';
    return '';
  }

  get publishDraftDisabled(): boolean {
    return this.publishDraftDisabledReason.length > 0;
  }

  get publishDraftDisabledReason(): string {
    const operationReason = this.blockingOperationReason;
    if (operationReason) return operationReason;
    if (!this.activeDraft) return 'Save a draft before publishing.';
    if (!this.canPublishDraft()) return 'Draft validation and regression tests must pass before publishing.';
    return '';
  }

  get regressionActionDisabled(): boolean {
    return this.regressionActionDisabledReason.length > 0;
  }

  get regressionActionDisabledReason(): string {
    if (this.isVersionAction) return 'A draft or publishing action is currently running.';
    if (this.isSavingRegressionTests) return 'Regression tests are currently saving.';
    return '';
  }

  get deleteDisabled(): boolean {
    return this.deleteDisabledReason.length > 0;
  }

  get deleteDisabledReason(): string {
    if (this.isDeleting) return 'Chatbot deletion is in progress.';
    return '';
  }

  rollbackDisabled(version: ChatbotVersionOptions): boolean {
    return this.rollbackDisabledReason(version).length > 0;
  }

  rollbackDisabledReason(version: ChatbotVersionOptions): string {
    if (this.isVersionAction) return 'A draft or publishing action is currently running.';
    if (version.status === 'draft') return 'Draft versions cannot be rolled back.';
    return '';
  }

  private get blockingOperationReason(): string {
    if (this.isSaving) return 'Settings are currently saving.';
    if (this.isDeleting) return 'Chatbot deletion is in progress.';
    if (this.isProposing) return 'Initial instruction generation is in progress.';
    if (this.isLocalizing) return 'Localization generation is in progress.';
    if (this.isVersionAction) return 'A draft or publishing action is currently running.';
    return '';
  }

  private ensurePersistableFormsValid(): boolean {
    const issue = this.getBlockingPersistenceIssue();
    if (!issue) {
      this.validationTargetPath = null;
      return true;
    }

    this.settingsForm.markAllAsTouched();
    this.messagesForm.markAllAsTouched();
    this.frontendForm.markAllAsTouched();
    this.widgetForm.markAllAsTouched();
    this.localizationVariantsForm.markAllAsTouched();
    this.validationTargetPath = issue.path;
    this.selectedIndex = issue.tabIndex;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.queryParamName]: issue.tabIndex },
      queryParamsHandling: 'merge'
    });
    this.snackBar.showError(`Fix "${issue.label}" before saving.`, 3000);
    return false;
  }

  private getBlockingPersistenceIssue(): PersistenceValidationIssue | null {
    return this.findFirstInvalidControl(this.settingsForm, 'settingsForm', false) ??
      this.getImageCompositionPersistenceIssue() ??
      this.getCustomFontPersistenceIssue() ??
      this.getQuickStartPersistenceIssue() ??
      this.findFirstInvalidControl(this.frontendForm, 'frontendForm', true) ??
      this.findFirstInvalidControl(this.widgetForm, 'widgetForm', true);
  }

  private getQuickStartPersistenceIssue(): PersistenceValidationIssue | null {
    const defaultIssue = this.findInvalidQuickStart(
      this.sampleMessages,
      'messagesForm.sampleMessages',
      'Default language');
    if (defaultIssue) return defaultIssue;

    for (let index = 0; index < this.localizationVariantsForm.length; index++) {
      const variant = this.localizationVariantsForm.at(index) as FormGroup;
      const messages = variant.get('sampleMessages') as FormArray | null;
      if (!messages) continue;

      const locale = `${variant.get('locale')?.value ?? 'Translation'}`.trim() || 'Translation';
      const issue = this.findInvalidQuickStart(
        messages,
        `localizationVariantsForm.${index}.sampleMessages`,
        locale);
      if (issue) return issue;
    }

    return null;
  }

  private findInvalidQuickStart(
    messages: FormArray,
    rootPath: string,
    localeLabel: string
  ): PersistenceValidationIssue | null {
    for (let index = 0; index < messages.length; index++) {
      const row = messages.at(index) as FormGroup;
      if (!row.invalid) continue;

      const raw = row.getRawValue() as Record<string, unknown>;
      const hasEnteredValue = Object.values(raw).some(value => `${value ?? ''}`.trim().length > 0);
      if (!messages.dirty && !row.dirty && !hasEnteredValue) continue;

      const field = row.get('headline')?.invalid ? 'headline' : 'description';
      return {
        path: `${rootPath}.${index}.${field}`,
        label: `${localeLabel} Quick Start ${index + 1} ${field}`,
        tabIndex: CHAT_WINDOW_TAB_INDEX
      };
    }

    return null;
  }

  private getCustomFontPersistenceIssue(): PersistenceValidationIssue | null {
    const styling = this.frontendForm.get('styling') as FormGroup | null;
    if (!styling) return null;

    const fontControlNames = [
      'fontProvider',
      'fontFamily',
      'fontFallback',
      'fontPreset',
      'fontFaceRegularUrl',
      'fontFaceMediumUrl',
      'fontFaceSemiBoldUrl',
      'fontFaceBoldUrl'
    ];
    if (!fontControlNames.some(name => styling.get(name)?.dirty)) {
      return null;
    }

    const validation = validateCustomFontConfiguration(styling.getRawValue());
    if (!validation.field) return null;

    return this.buildPersistenceValidationIssue(`frontendForm.styling.${validation.field}`);
  }

  private getImageCompositionValidation(
    existingOptions?: Partial<IImageCompositionOptions> | null
  ) {
    const resolvedExistingOptions = existingOptions ?? this.chatbot?.frontEndOptions?.imageComposition;
    const imageCompositionValue = this.frontendForm.get('imageComposition')?.getRawValue() as IImageCompositionFormValue | undefined;
    const attachmentsEnabled = this.frontendForm.get('generalSettings.attachmentsEnabled')?.value === true;
    return validateAndNormalizeImageCompositionOptions(
      imageCompositionValue,
      resolvedExistingOptions,
      attachmentsEnabled,
      this.imageCompositionRuntimeReady,
      {
        preserveExistingEnabled: this.shouldPreserveLoadedImageCompositionEnabled(resolvedExistingOptions)
      }
    );
  }

  private shouldPreserveLoadedImageCompositionEnabled(
    existingOptions?: Partial<IImageCompositionOptions> | null
  ): boolean {
    const activationControl = this.frontendForm.get('imageComposition.enabled');
    return !this.imageCompositionRuntimeReady
      && existingOptions?.enabled === true
      && activationControl?.dirty !== true;
  }

  private getImageCompositionPersistenceIssue(): PersistenceValidationIssue | null {
    const validation = this.getImageCompositionValidation();
    if (validation.errors.length === 0) {
      return null;
    }

    const path = validation.categoryError
      ? 'frontendForm.imageComposition.eligibleCategoriesText'
      : validation.disclaimerError
        ? 'frontendForm.imageComposition.disclaimerText'
        : 'frontendForm.imageComposition.enabled';

    return this.buildPersistenceValidationIssue(path);
  }

  private hasBlockingDirtyInvalidControl(form: AbstractControl): boolean {
    return this.findFirstInvalidControl(form, '', true, false) !== null;
  }

  private findFirstInvalidControl(
    control: AbstractControl,
    rootPath: string,
    requireDirty: boolean,
    includeMetadata: boolean = true
  ): PersistenceValidationIssue | null {
    if (control instanceof FormControl) {
      if (!control.invalid || (requireDirty && !control.dirty)) {
        return null;
      }
      return includeMetadata ? this.buildPersistenceValidationIssue(rootPath) : {
        path: rootPath,
        label: rootPath,
        tabIndex: SETTINGS_TAB_INDEX
      };
    }

    const nextControls = control instanceof FormArray
      ? control.controls.map((childControl, index) => [String(index), childControl] as const)
      : Object.entries((control as FormGroup).controls ?? {});

    for (const [key, childControl] of nextControls) {
      const childPath = rootPath ? `${rootPath}.${key}` : key;
      const issue = this.findFirstInvalidControl(childControl, childPath, requireDirty, includeMetadata);
      if (issue) {
        return issue;
      }
    }

    return null;
  }

  private buildPersistenceValidationIssue(path: string): PersistenceValidationIssue {
    const metadata = PERSISTENCE_VALIDATION_LABELS[path];
    if (metadata) {
      return {
        path,
        label: metadata.label,
        tabIndex: metadata.tabIndex
      };
    }

    const [, ...segments] = path.split('.');
    const label = segments[segments.length - 1]
      ?.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^\w/, char => char.toUpperCase()) || 'setting';

    return {
      path,
      label,
      tabIndex: path.startsWith('widgetForm.') ? WIDGET_TAB_INDEX : path.startsWith('frontendForm.') ? CHAT_WINDOW_TAB_INDEX : SETTINGS_TAB_INDEX
    };
  }

  private buildAutoCloseOptions(): IAutoClose {
    const autoClose = this.widgetForm.value.autoClose ?? {};
    const devices = autoClose.devices ?? {};
    const parsedDelay = Number(autoClose.delayMs);
    const delayMs = Number.isFinite(parsedDelay) ? Math.max(0, parsedDelay) : 2000;

    return {
      enabled: autoClose.enabled ?? true,
      delayMs,
      devices: {
        desktop: devices.desktop ?? false,
        tablet: devices.tablet ?? false,
        mobile: devices.mobile ?? true
      },
      closeOnSameClientReferrer: autoClose.closeOnSameClientReferrer ?? true
    };
  }

  private persistChatbotSettings(markHubSpotPristine: boolean = false, afterSave?: () => void): void {
    if (!this.chatbot) return;
    if (!this.ensurePersistableFormsValid()) return;

    this.isSaving = true;

    Object.assign(this.chatbot, this.settingsForm.value);
    // Ensure dynamic widget fields are present on outgoing object
    (this.chatbot as any).dynamicWidgetText = this.settingsForm.value.dynamicWidgetText ?? false;
    (this.chatbot as any).dynamicWidgetTextInstruction = this.settingsForm.value.dynamicWidgetTextInstruction ?? '';
    (this.chatbot as any).aiProductCardsEnabled = this.settingsForm.value.aiProductCardsEnabled ?? false;
    (this.chatbot as any).aiProductCardsInstruction = this.settingsForm.value.aiProductCardsInstruction ?? '';
    delete (this.chatbot as any).conversationCategories;
    const retentionRaw = this.settingsForm.value.conversationRetentionDays;
    (this.chatbot as any).conversationRetentionDays =
      retentionRaw === null || retentionRaw === '' ? null : Number(retentionRaw);

    // Update Widget Form Data
    this.chatbot.widgetOptions.text.aiMessage = this.widgetForm.value.text;
    this.chatbot.widgetOptions.launcherShape = this.normalizeLauncherShape(this.widgetForm.value.launcherShape);
    this.chatbot.widgetOptions.styling = this.widgetForm.get('styling')?.value;
    this.chatbot.widgetOptions.popupTailEnabled = this.widgetForm.value.popupTailEnabled !== false;
    this.chatbot.widgetOptions.hoverRevealEnabled = this.widgetForm.value.hoverRevealEnabled ?? true;
    this.chatbot.widgetOptions.positionMode = this.widgetForm.value.positionMode ?? 'fixed';
    this.chatbot.widgetOptions.mobilePopupOnce = this.widgetForm.value.mobilePopupOnce ?? false;
    this.chatbot.widgetOptions.autoClose = this.buildAutoCloseOptions();
    const scrollReveal = this.widgetForm.value.scrollReveal ?? {};
    const scrollDevices = scrollReveal.devices ?? {};
    const parsedThreshold = Number(scrollReveal.thresholdPx);
    const normalizedThreshold = Number.isFinite(parsedThreshold) ? Math.max(0, parsedThreshold) : 800;
    this.chatbot.widgetOptions.scrollReveal = {
      enabled: scrollReveal.enabled ?? false,
      thresholdPx: normalizedThreshold,
      devices: {
        desktop: scrollDevices.desktop ?? true,
        tablet: scrollDevices.tablet ?? true,
        mobile: scrollDevices.mobile ?? true
      }
    };
    this.chatbot.widgetOptions.images.aiLogo1 = this.widgetForm.value.images.aiLogo1;
    this.chatbot.widgetOptions.images.aiLogo2 = this.widgetForm.value.images.aiLogo2;
    this.chatbot.widgetOptions.images.logoSwapEnabled = this.widgetForm.value.images.logoSwapEnabled;
    this.chatbot.widgetOptions.images.launcherLogoPadding = this.widgetForm.value.images.launcherLogoPadding || '0px';
    const widgetColors = this.widgetForm.value.colors;
    this.chatbot.widgetOptions.colors = {
      ...this.chatbot.widgetOptions.colors,
      ...widgetColors
    };

    // Update Frontend Form Data
    this.chatbot.frontEndOptions.name = this.frontendForm.value.generalSettings.name;
    this.chatbot.frontEndOptions.logo = this.frontendForm.value.generalSettings.logo;
    this.chatbot.frontEndOptions.headerDisplayMode = this.normalizeHeaderDisplayMode(this.frontendForm.value.generalSettings.headerDisplayMode);
    this.chatbot.frontEndOptions.layoutPreset = this.normalizeLayoutPreset(this.frontendForm.value.generalSettings.layoutPreset);
    this.chatbot.frontEndOptions.zIndex = normalizeWidgetZIndex(this.frontendForm.value.generalSettings.zIndex);
    this.chatbot.frontEndOptions.logoWidth = this.frontendForm.value.generalSettings.logoWidth;
    this.chatbot.frontEndOptions.voiceEnabled = this.frontendForm.value.generalSettings.voiceEnabled;
    this.chatbot.frontEndOptions.attachmentsEnabled = this.frontendForm.value.generalSettings.attachmentsEnabled;
    this.chatbot.frontEndOptions.imageComposition = this.getImageCompositionValidation(
      this.chatbot.frontEndOptions.imageComposition
    ).normalized;
    this.chatbot.frontEndOptions.introMessage = this.frontendForm.value.generalSettings.introMessage;
    this.chatbot.frontEndOptions.firstMessage = this.frontendForm.value.generalSettings.firstMessage;
    this.chatbot.frontEndOptions.inputPlaceholder = this.frontendForm.value.generalSettings.inputPlaceholder;
    this.chatbot.frontEndOptions.submitButtonIcon = this.frontendForm.value.generalSettings.submitButtonIcon || '';
    this.chatbot.frontEndOptions.resetButtonIcon = this.frontendForm.value.generalSettings.resetButtonIcon || '';
    this.chatbot.frontEndOptions.attachButtonIcon = this.frontendForm.value.generalSettings.attachButtonIcon || '';
    this.chatbot.frontEndOptions.voiceButtonIcon = this.frontendForm.value.generalSettings.voiceButtonIcon || '';
    this.chatbot.frontEndOptions.compactSubmitButtonIcon = this.frontendForm.value.generalSettings.compactSubmitButtonIcon || '';
    this.chatbot.frontEndOptions.compactResetButtonIcon = this.frontendForm.value.generalSettings.compactResetButtonIcon || '';
    this.chatbot.frontEndOptions.resetButtonTooltip = (this.frontendForm.value.generalSettings.resetButtonTooltip || '').trim();
    this.chatbot.frontEndOptions.resetButtonConfirmationEnabled =
      this.frontendForm.value.generalSettings.resetButtonConfirmationEnabled === true;
    this.chatbot.frontEndOptions.resetButtonConfirmationText =
      (this.frontendForm.value.generalSettings.resetButtonConfirmationText || '').trim();
    this.chatbot.frontEndOptions.sampleMessagesTitle = this.frontendForm.value.generalSettings.sampleMessagesTitle;
    this.chatbot.frontEndOptions.sampleMessagesPresentation = this.frontendForm.value.generalSettings.sampleMessagesPresentation;
    this.chatbot.frontEndOptions.sampleMessagesHoverBorderColor = (this.frontendForm.value.generalSettings.sampleMessagesHoverBorderColor || '').trim();
    this.chatbot.frontEndOptions.sampleMessagesClickAnimationEnabled =
      this.frontendForm.value.generalSettings.sampleMessagesClickAnimationEnabled === true;
    this.chatbot.frontEndOptions.sampleMessagesClickAnimationDelayMs =
      this.normalizeSampleMessagesClickAnimationDelay(this.frontendForm.value.generalSettings.sampleMessagesClickAnimationDelayMs);

    // Update Product Card Settings
    this.chatbot.frontEndOptions.cardType = this.frontendForm.value.productCardSettings.cardType;
    this.chatbot.frontEndOptions.productCardPresentation =
      this.normalizeProductCardPresentation(this.frontendForm.value.productCardSettings.productCardPresentation);
    this.chatbot.frontEndOptions.productCardPresentationMode =
      this.normalizeProductCardPresentationMode(this.frontendForm.value.productCardSettings.productCardPresentationMode);
    this.chatbot.frontEndOptions.productCardDynamicPresentations =
      this.normalizeProductCardDynamicPresentations(this.frontendForm.value.productCardSettings.productCardDynamicPresentations);
    this.chatbot.frontEndOptions.addToCartEnabled = this.frontendForm.value.productCardSettings.addToCartEnabled === true;
    this.chatbot.frontEndOptions.productCardEcommerceTrackingEnabled =
      this.frontendForm.value.productCardSettings.productCardEcommerceTrackingEnabled === true;
    this.chatbot.frontEndOptions.ga4EcommerceEventsEnabled =
      this.frontendForm.value.productCardSettings.ga4EcommerceEventsEnabled === true;
    this.chatbot.frontEndOptions.cardLinkUrl = this.frontendForm.value.productCardSettings.cardLinkUrl;
    this.chatbot.frontEndOptions.cardUtmString = this.frontendForm.value.productCardSettings.cardUtmString;
    this.chatbot.frontEndOptions.cardLocale = this.frontendForm.value.productCardSettings.cardLocale;
    this.chatbot.frontEndOptions.cardCurrency = this.frontendForm.value.productCardSettings.cardCurrency;
    this.chatbot.frontEndOptions.productCardTextOverrides =
      parseProductCardTextOverrides(this.frontendForm.value.productCardSettings.productCardTextOverrides) ?? {};
    this.chatbot.frontEndOptions.productCardDetailsLabel =
      (this.frontendForm.value.productCardSettings.productCardDetailsLabel || '').trim();
    this.chatbot.frontEndOptions.productCardTestDriveLabel =
      (this.frontendForm.value.productCardSettings.productCardTestDriveLabel || '').trim();

    // Update Dropdown Settings
    this.chatbot.frontEndOptions.dropdownEnabled = this.frontendForm.value.dropdownSettings.dropdownEnabled;
    this.chatbot.frontEndOptions.dropdownBtnText = this.frontendForm.value.dropdownSettings.dropdownBtnText;
    this.chatbot.frontEndOptions.dropdownBtnTextClose = this.frontendForm.value.dropdownSettings.dropdownBtnTextClose;
    this.chatbot.frontEndOptions.dropdownMessage = this.frontendForm.value.dropdownSettings.dropdownMessage;
    this.chatbot.frontEndOptions.dropdownBtnIconEnabled = this.frontendForm.value.dropdownSettings.dropdownBtnIconEnabled;

    // Update Chat Avatar
    this.chatbot.frontEndOptions.chatAvatar = this.frontendForm.value.chatAvatar;
      this.chatbot.frontEndOptions.chatAvatarEnabled = this.frontendForm.value.chatAvatarEnabled !== false;

    // Update Styling configuration
    const stylingForm = this.frontendForm.value.styling;
    const normalisedStyling = this.normalizeStylingForSave({ ...this.chatbot.frontEndOptions.styling, ...stylingForm } as IStyling & Record<string, any>);
    // Ensure width persists as string for API compatibility
    if (normalisedStyling.chatBotWidth !== undefined && normalisedStyling.chatBotWidth !== null) {
        normalisedStyling.chatBotWidth = String(normalisedStyling.chatBotWidth);
    }
    this.chatbot.frontEndOptions.styling = normalisedStyling;

    // Update CSS and JS
    this.chatbot.frontEndOptions.css = this.frontendForm.value.css;
    this.chatbot.frontEndOptions.js = this.frontendForm.value.js;

    // Update cookies to collect (array of strings)
    const cookiesArr: string[] = this.settingsForm.value.cookiesToCollect || [];
    this.chatbot.cookiesToCollect = Array.from(new Set(
      (cookiesArr || []).map((s: string) => (s || '').trim()).filter((s: string) => !!s)
    ));
    this.chatbot.regressionTestCases = this.normalizeRegressionTestCases(this.regressionTestCases);

    this.chatbot.frontEndOptions.errorMessages = this.errorMessages.value.filter((msg: ITemplateMessage) => !!msg.message);
    this.chatbot.frontEndOptions.waitMessages = this.waitMessages.value.filter((msg: ITemplateMessage) => !!msg.message);
    this.chatbot.frontEndOptions.sampleMessages = this.normalizeSampleMessages(this.sampleMessages.value);

    const defaultLocale = (this.localizationForm.value.defaultLocale ?? '').toString().trim();
    const supportedLocales = Array.from(new Set([
      defaultLocale,
      ...((this.localizationForm.value.supportedLocales ?? []) as string[])
    ].map(locale => (locale || '').trim()).filter(locale => !!locale)));
    this.ensureSupportedLocalizationVariantForms(defaultLocale, supportedLocales);
    this.chatbot.localization = {
      ...(this.chatbot.localization ?? { variants: {} }),
      defaultLocale,
      supportedLocales,
      autoGenerate: this.localizationForm.value.autoGenerate ?? false,
      variants: this.buildLocalizationVariantsPayload()
    };

    // Send Save Request
    this.chatbotsService.saveAsync(this.chatbot).subscribe(saved => {
      if (saved?._etag && this.chatbot) {
        this.chatbot._etag = saved._etag;
      }
      this.snackBar.showSuccess('Settings saved successfully', 3000);
      if (markHubSpotPristine) {
        this.hubspotForm.markAsPristine();
      }
      this.localizationForm.markAsPristine();
      this.localizationVariantsForm.markAsPristine();
      this.isSaving = false;
      afterSave?.();
    }, error => {
      console.error('Error saving settings', error);
      const message = error?.error?.message || error?.error?.error || 'Error saving settings';
      this.snackBar.showError(message, 3000);
      this.isSaving = false;
    });
  }

  async saveDraft(): Promise<void> {
    if (!this.chatbot) return;
    if (!this.ensurePersistableFormsValid()) return;

    this.isVersionAction = true;
    try {
      const draft = this.activeDraft ?? await this.chatbotsService.createDraftAsync(this.chatbot.id).toPromise();
      if (!draft?.id) throw new Error('Draft creation failed.');

      const request: UpdateChatbotDraftRequest = {
        config: this.buildChatbotDraftConfig()
      };
      this.activeDraft = await this.chatbotsService.updateDraftAsync(this.chatbot.id, draft.id, request).toPromise() ?? null;
      await this.loadVersions();
      this.markFormsPristine();
      this.refsHasChanged = false;
      this.snackBar.showSuccess('Draft saved and tested.', 3000);
    } catch (error) {
      console.error('Error saving chatbot draft', error);
      this.snackBar.showError('Error saving draft.', 3000);
    } finally {
      this.isVersionAction = false;
    }
  }

  async publishDraft(): Promise<void> {
    if (!this.chatbot?.id || !this.activeDraft?.id) return;
    this.isVersionAction = true;
    try {
      await this.chatbotsService.publishDraftAsync(this.chatbot.id, this.activeDraft.id).toPromise();
      await this.loadData();
      this.snackBar.showSuccess('Draft published.', 3000);
    } catch (error) {
      console.error('Error publishing chatbot draft', error);
      this.snackBar.showError('Draft publish failed. Check validation and tests.', 4000);
    } finally {
      this.isVersionAction = false;
    }
  }

  async rollback(version: ChatbotVersionOptions): Promise<void> {
    if (!this.chatbot?.id || !version?.id) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Rollback chatbot?',
        message: `This will publish version ${version.id} as the active chatbot configuration.`,
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Rollback',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result || !this.chatbot?.id) return;
      this.isVersionAction = true;
      try {
        await this.chatbotsService.rollbackAsync(this.chatbot.id, version.id).toPromise();
        await this.loadData();
        this.snackBar.showSuccess('Chatbot rolled back.', 3000);
      } catch (error) {
        console.error('Error rolling back chatbot', error);
        this.snackBar.showError('Rollback failed.', 3000);
      } finally {
        this.isVersionAction = false;
      }
    });
  }

  canPublishDraft(): boolean {
    return !!this.activeDraft?.validationResult?.passed && !!this.activeDraft?.testRunSummary?.passed;
  }

  addRegressionTestCase(): void {
    this.regressionTestCases = [
      ...this.regressionTestCases,
      {
        name: `Regression ${this.regressionTestCases.length + 1}`,
        expectedClassificationPromptContains: '',
        expectedAssistantInstructionsContains: '',
        expectedAssistantRef: '',
        expectedBaseUrlContains: '',
        expectedDefaultLocale: this.localizationForm.value.defaultLocale ?? '',
        enabled: true
      }
    ];
  }

  removeRegressionTestCase(index: number): void {
    this.regressionTestCases = this.regressionTestCases.filter((_, currentIndex) => currentIndex !== index);
  }

  async saveRegressionTests(): Promise<void> {
    if (!this.chatbot?.id) return;
    this.isSavingRegressionTests = true;
    try {
      const testCases = this.normalizeRegressionTestCases(this.regressionTestCases);
      const saved = await this.chatbotsService.saveRegressionTestsAsync(this.chatbot.id, testCases).toPromise();
      this.regressionTestCases = this.cloneRegressionTestCases(saved ?? []);
      this.chatbot.regressionTestCases = this.cloneRegressionTestCases(this.regressionTestCases);
      this.snackBar.showSuccess('Regression tests saved.', 3000);
    } catch (error) {
      console.error('Error saving chatbot regression tests', error);
      this.snackBar.showError('Error saving regression tests.', 3000);
    } finally {
      this.isSavingRegressionTests = false;
    }
  }

  private buildChatbotDraftConfig(): Partial<IChatbotOptions> {
    if (!this.chatbot) return {};
    const draft = JSON.parse(JSON.stringify(this.chatbot)) as IChatbotOptions;
    Object.assign(draft, this.settingsForm.value);
    delete (draft as any).conversationCategories;
    const retentionRaw = this.settingsForm.value.conversationRetentionDays;
    draft.conversationRetentionDays = retentionRaw === null || retentionRaw === '' ? null : Number(retentionRaw);
    draft.cookiesToCollect = Array.from(new Set(
      ((this.settingsForm.value.cookiesToCollect || []) as string[])
        .map(value => (value || '').trim())
        .filter(value => !!value)
    ));
    draft.assistantRefs = [...(this.chatbot.assistantRefs ?? [])];
    draft.regressionTestCases = this.normalizeRegressionTestCases(this.regressionTestCases);

    draft.widgetOptions.text.aiMessage = this.widgetForm.value.text;
    draft.widgetOptions.hoverRevealEnabled = this.widgetForm.value.hoverRevealEnabled ?? true;
    draft.widgetOptions.positionMode = this.widgetForm.value.positionMode ?? 'fixed';
    draft.widgetOptions.mobilePopupOnce = this.widgetForm.value.mobilePopupOnce ?? false;
    draft.widgetOptions.autoClose = this.buildAutoCloseOptions();
    const scrollReveal = this.widgetForm.value.scrollReveal ?? {};
    const scrollDevices = scrollReveal.devices ?? {};
    draft.widgetOptions.scrollReveal = {
      enabled: scrollReveal.enabled ?? false,
      thresholdPx: Math.max(0, Number(scrollReveal.thresholdPx) || 800),
      devices: {
        desktop: scrollDevices.desktop ?? true,
        tablet: scrollDevices.tablet ?? true,
        mobile: scrollDevices.mobile ?? true
      }
    };
    draft.widgetOptions.images.aiLogo1 = this.widgetForm.value.images.aiLogo1;
    draft.widgetOptions.images.aiLogo2 = this.widgetForm.value.images.aiLogo2;
    draft.widgetOptions.images.logoSwapEnabled = this.widgetForm.value.images.logoSwapEnabled;
    draft.widgetOptions.images.launcherLogoPadding = this.widgetForm.value.images.launcherLogoPadding || '0px';
    draft.widgetOptions.launcherShape = this.normalizeLauncherShape(this.widgetForm.value.launcherShape);
    draft.widgetOptions.styling = this.widgetForm.get('styling')?.value;
    draft.widgetOptions.popupTailEnabled = this.widgetForm.value.popupTailEnabled !== false;
    draft.widgetOptions.colors = {
      ...draft.widgetOptions.colors,
      ...this.widgetForm.value.colors
    };

    draft.frontEndOptions.name = this.frontendForm.value.generalSettings.name;
    draft.frontEndOptions.logo = this.frontendForm.value.generalSettings.logo;
    draft.frontEndOptions.headerDisplayMode = this.normalizeHeaderDisplayMode(this.frontendForm.value.generalSettings.headerDisplayMode);
    draft.frontEndOptions.layoutPreset = this.normalizeLayoutPreset(this.frontendForm.value.generalSettings.layoutPreset);
    draft.frontEndOptions.zIndex = normalizeWidgetZIndex(this.frontendForm.value.generalSettings.zIndex);
    draft.frontEndOptions.logoWidth = this.frontendForm.value.generalSettings.logoWidth;
    draft.frontEndOptions.voiceEnabled = this.frontendForm.value.generalSettings.voiceEnabled;
    draft.frontEndOptions.attachmentsEnabled = this.frontendForm.value.generalSettings.attachmentsEnabled;
    draft.frontEndOptions.imageComposition = this.getImageCompositionValidation(
      draft.frontEndOptions.imageComposition
    ).normalized;
    draft.frontEndOptions.introMessage = this.frontendForm.value.generalSettings.introMessage;
    draft.frontEndOptions.firstMessage = this.frontendForm.value.generalSettings.firstMessage;
    draft.frontEndOptions.inputPlaceholder = this.frontendForm.value.generalSettings.inputPlaceholder;
    draft.frontEndOptions.submitButtonIcon = this.frontendForm.value.generalSettings.submitButtonIcon || '';
    draft.frontEndOptions.resetButtonIcon = this.frontendForm.value.generalSettings.resetButtonIcon || '';
    draft.frontEndOptions.attachButtonIcon = this.frontendForm.value.generalSettings.attachButtonIcon || '';
    draft.frontEndOptions.voiceButtonIcon = this.frontendForm.value.generalSettings.voiceButtonIcon || '';
    draft.frontEndOptions.compactSubmitButtonIcon = this.frontendForm.value.generalSettings.compactSubmitButtonIcon || '';
    draft.frontEndOptions.compactResetButtonIcon = this.frontendForm.value.generalSettings.compactResetButtonIcon || '';
    draft.frontEndOptions.resetButtonTooltip = (this.frontendForm.value.generalSettings.resetButtonTooltip || '').trim();
    draft.frontEndOptions.resetButtonConfirmationEnabled =
      this.frontendForm.value.generalSettings.resetButtonConfirmationEnabled === true;
    draft.frontEndOptions.resetButtonConfirmationText =
      (this.frontendForm.value.generalSettings.resetButtonConfirmationText || '').trim();
    draft.frontEndOptions.sampleMessagesTitle = this.frontendForm.value.generalSettings.sampleMessagesTitle;
    draft.frontEndOptions.sampleMessagesPresentation = this.frontendForm.value.generalSettings.sampleMessagesPresentation;
    draft.frontEndOptions.sampleMessagesHoverBorderColor = (this.frontendForm.value.generalSettings.sampleMessagesHoverBorderColor || '').trim();
    draft.frontEndOptions.sampleMessagesClickAnimationEnabled =
      this.frontendForm.value.generalSettings.sampleMessagesClickAnimationEnabled === true;
    draft.frontEndOptions.sampleMessagesClickAnimationDelayMs =
      this.normalizeSampleMessagesClickAnimationDelay(this.frontendForm.value.generalSettings.sampleMessagesClickAnimationDelayMs);
    draft.frontEndOptions.cardType = this.frontendForm.value.productCardSettings.cardType;
    draft.frontEndOptions.productCardPresentation = this.normalizeProductCardPresentation(this.frontendForm.value.productCardSettings.productCardPresentation);
    draft.frontEndOptions.productCardPresentationMode = this.normalizeProductCardPresentationMode(this.frontendForm.value.productCardSettings.productCardPresentationMode);
    draft.frontEndOptions.productCardDynamicPresentations = this.normalizeProductCardDynamicPresentations(this.frontendForm.value.productCardSettings.productCardDynamicPresentations);
    draft.frontEndOptions.addToCartEnabled = this.frontendForm.value.productCardSettings.addToCartEnabled === true;
    draft.frontEndOptions.productCardEcommerceTrackingEnabled =
      this.frontendForm.value.productCardSettings.productCardEcommerceTrackingEnabled === true;
    draft.frontEndOptions.ga4EcommerceEventsEnabled =
      this.frontendForm.value.productCardSettings.ga4EcommerceEventsEnabled === true;
    draft.frontEndOptions.cardLinkUrl = this.frontendForm.value.productCardSettings.cardLinkUrl;
    draft.frontEndOptions.cardUtmString = this.frontendForm.value.productCardSettings.cardUtmString;
    draft.frontEndOptions.cardLocale = this.frontendForm.value.productCardSettings.cardLocale;
    draft.frontEndOptions.cardCurrency = this.frontendForm.value.productCardSettings.cardCurrency;
    draft.frontEndOptions.productCardTextOverrides =
      parseProductCardTextOverrides(this.frontendForm.value.productCardSettings.productCardTextOverrides) ?? {};
    draft.frontEndOptions.productCardDetailsLabel =
      (this.frontendForm.value.productCardSettings.productCardDetailsLabel || '').trim();
    draft.frontEndOptions.productCardTestDriveLabel =
      (this.frontendForm.value.productCardSettings.productCardTestDriveLabel || '').trim();
    draft.frontEndOptions.dropdownEnabled = this.frontendForm.value.dropdownSettings.dropdownEnabled;
    draft.frontEndOptions.dropdownBtnText = this.frontendForm.value.dropdownSettings.dropdownBtnText;
    draft.frontEndOptions.dropdownBtnTextClose = this.frontendForm.value.dropdownSettings.dropdownBtnTextClose;
    draft.frontEndOptions.dropdownMessage = this.frontendForm.value.dropdownSettings.dropdownMessage;
    draft.frontEndOptions.dropdownBtnIconEnabled = this.frontendForm.value.dropdownSettings.dropdownBtnIconEnabled;
    draft.frontEndOptions.chatAvatar = this.frontendForm.value.chatAvatar;
      draft.frontEndOptions.chatAvatarEnabled = this.frontendForm.value.chatAvatarEnabled !== false;
    draft.frontEndOptions.styling = this.normalizeStylingForSave({
      ...draft.frontEndOptions.styling,
      ...this.frontendForm.value.styling,
      chatBotWidth: String(this.frontendForm.value.styling?.chatBotWidth ?? '1200')
    } as IStyling & Record<string, any>);
    draft.frontEndOptions.css = this.frontendForm.value.css;
    draft.frontEndOptions.js = this.frontendForm.value.js;
    draft.frontEndOptions.errorMessages = this.errorMessages.value.filter((msg: ITemplateMessage) => !!msg.message);
    draft.frontEndOptions.waitMessages = this.waitMessages.value.filter((msg: ITemplateMessage) => !!msg.message);
    draft.frontEndOptions.sampleMessages = this.normalizeSampleMessages(this.sampleMessages.value);

    const defaultLocale = (this.localizationForm.value.defaultLocale ?? '').toString().trim();
    const supportedLocales = Array.from(new Set([
      defaultLocale,
      ...((this.localizationForm.value.supportedLocales ?? []) as string[])
    ].map(locale => (locale || '').trim()).filter(locale => !!locale)));
    this.ensureSupportedLocalizationVariantForms(defaultLocale, supportedLocales);
    draft.localization = {
      ...(draft.localization ?? { variants: {} }),
      defaultLocale,
      supportedLocales,
      autoGenerate: this.localizationForm.value.autoGenerate ?? false,
      variants: this.buildLocalizationVariantsPayload()
    };
    const hubSpotSyncPayload = this.buildHubSpotSyncPayload();
    draft.hubSpotSync = {
      ...(draft.hubSpotSync ?? {}),
      enabled: hubSpotSyncPayload.enabled,
      inboxId: hubSpotSyncPayload.inboxId ?? '',
      handoverPolicy: hubSpotSyncPayload.handoverPolicy ?? 'auto',
      pauseBotOnHandover: hubSpotSyncPayload.pauseBotOnHandover ?? true
    };
    return draft;
  }

  private markFormsPristine(): void {
    this.settingsForm.markAsPristine();
    this.messagesForm.markAsPristine();
    this.frontendForm.markAsPristine();
    this.widgetForm.markAsPristine();
    this.localizationForm.markAsPristine();
    this.localizationVariantsForm.markAsPristine();
    this.hubspotForm.markAsPristine();
  }

  private cloneRegressionTestCases(testCases: ChatbotRegressionTestCase[]): ChatbotRegressionTestCase[] {
    return testCases.map(testCase => ({...testCase, enabled: testCase.enabled ?? true}));
  }

  private normalizeRegressionTestCases(testCases: ChatbotRegressionTestCase[]): ChatbotRegressionTestCase[] {
    return testCases
      .filter(testCase =>
        !!testCase.name?.trim() ||
        (testCase.expectedClassificationPromptContains !== null && testCase.expectedClassificationPromptContains !== undefined) ||
        !!testCase.forbiddenClassificationPromptContains?.trim() ||
        (testCase.expectedAssistantInstructionsContains !== null && testCase.expectedAssistantInstructionsContains !== undefined) ||
        !!testCase.forbiddenAssistantInstructionsContains?.trim() ||
        !!testCase.expectedAssistantRef?.trim() ||
        (testCase.expectedBaseUrlContains !== null && testCase.expectedBaseUrlContains !== undefined) ||
        !!testCase.expectedDefaultLocale?.trim() ||
        (testCase.expectedAiProductCardsEnabled !== null && testCase.expectedAiProductCardsEnabled !== undefined))
      .map(testCase => ({
        name: testCase.name?.trim() ?? '',
        expectedClassificationPromptContains: testCase.expectedClassificationPromptContains ?? null,
        forbiddenClassificationPromptContains: testCase.forbiddenClassificationPromptContains?.trim() || null,
        expectedAssistantInstructionsContains: testCase.expectedAssistantInstructionsContains ?? null,
        forbiddenAssistantInstructionsContains: testCase.forbiddenAssistantInstructionsContains?.trim() || null,
        expectedAssistantRef: testCase.expectedAssistantRef?.trim() || null,
        expectedBaseUrlContains: testCase.expectedBaseUrlContains ?? null,
        expectedDefaultLocale: testCase.expectedDefaultLocale?.trim() || null,
        expectedAiProductCardsEnabled: testCase.expectedAiProductCardsEnabled ?? null,
        enabled: testCase.enabled ?? true
      }));
  }

  generateLocalization(force: boolean = false): void {
    if (!this.chatbot) return;
    const defaultLocale = (this.localizationForm.value.defaultLocale ?? '').toString().trim();
    if (!defaultLocale) {
      this.snackBar.showError('Select a default locale before generating translations', 3000);
      return;
    }

    const runGeneration = () => {
      if (!this.chatbot) return;
      this.isLocalizing = true;
      this.chatbotsService.localizeAsync(this.chatbot.id, force).subscribe({
        next: (result) => {
          const generated = result.generatedLocales?.length ?? 0;
          const skipped = result.skippedLocales?.length ?? 0;
          this.snackBar.showSuccess(`Generated ${generated} locale(s); skipped ${skipped}.`, 3000);
          this.loadData();
        },
        error: (error) => {
          console.error('Error generating localizations', error);
          const message = error?.error?.message || error?.error?.error || 'Error generating translations';
          this.snackBar.showError(message, 3000);
          this.isLocalizing = false;
        },
        complete: () => {
          this.isLocalizing = false;
        }
      });
    };

    if (this.settingsForm.dirty || this.messagesForm.dirty || this.frontendForm.dirty || this.widgetForm.dirty || this.localizationForm.dirty || this.localizationVariantsForm.dirty) {
      this.persistChatbotSettings(false, runGeneration);
      return;
    }

    runGeneration();
  }

  private populateLocalizationVariants(variants: Record<string, any>): void {
    this.localizationVariantsForm.clear();
    Object.entries(variants)
      .sort(([left], [right]) => left.localeCompare(right))
      .forEach(([locale, variant]) => {
        const frontEnd = variant?.frontEndOptions ?? variant?.FrontEndOptions ?? {};
        const widget = variant?.widgetOptions ?? variant?.WidgetOptions ?? {};
        const widgetText = widget?.text?.aiMessage ?? widget?.Text?.AiMessage ?? '';
        const errorMessages = (frontEnd?.errorMessages ?? frontEnd?.ErrorMessages ?? [])
          .map((msg: any) => msg?.message ?? msg?.Message ?? '')
          .filter((message: string) => !!message)
          .join('\n');
        const waitMessages = (frontEnd?.waitMessages ?? frontEnd?.WaitMessages ?? [])
          .map((msg: any) => msg?.message ?? msg?.Message ?? '')
          .filter((message: string) => !!message)
          .join('\n');
        const sampleMessages = (frontEnd?.sampleMessages ?? frontEnd?.SampleMessages ?? [])
          .map((msg: any) => ({
            headline: msg?.headline ?? msg?.Headline ?? '',
            description: msg?.description ?? msg?.Description ?? '',
            imageUrl: msg?.imageUrl ?? msg?.ImageUrl ?? '',
            imageAlt: msg?.imageAlt ?? msg?.ImageAlt ?? msg?.category ?? msg?.Category ?? ''
          }));

        this.localizationVariantsForm.push(this.createLocalizationVariantGroup(locale, {
          generatedFromHash: variant?.generatedFromHash ?? variant?.GeneratedFromHash ?? '',
          generatedAt: variant?.generatedAt ?? variant?.GeneratedAt ?? '',
          widgetText,
          introMessage: frontEnd?.introMessage ?? frontEnd?.IntroMessage ?? '',
          firstMessage: frontEnd?.firstMessage ?? frontEnd?.FirstMessage ?? '',
          inputPlaceholder: frontEnd?.inputPlaceholder ?? frontEnd?.InputPlaceholder ?? '',
          dropdownBtnText: frontEnd?.dropdownBtnText ?? frontEnd?.DropdownBtnText ?? '',
          dropdownBtnTextClose: frontEnd?.dropdownBtnTextClose ?? frontEnd?.DropdownBtnTextClose ?? '',
          dropdownMessage: frontEnd?.dropdownMessage ?? frontEnd?.DropdownMessage ?? '',
          addToCartEnabled: frontEnd?.addToCartEnabled ?? frontEnd?.AddToCartEnabled ?? null,
          errorMessages,
          waitMessages,
          sampleMessages
        }));
      });
    this.localizationVariantsForm.markAsPristine();
  }

  private createLocalizationVariantGroup(locale: string, variant: Record<string, any> = {}): FormGroup {
    const sampleMessages = Array.isArray(variant['sampleMessages']) ? variant['sampleMessages'] : [];
    return this.fb.group({
      locale: [locale],
      generatedFromHash: [variant['generatedFromHash'] ?? ''],
      generatedAt: [variant['generatedAt'] ?? ''],
      widgetText: [variant['widgetText'] ?? ''],
      introMessage: [variant['introMessage'] ?? ''],
      firstMessage: [variant['firstMessage'] ?? ''],
      inputPlaceholder: [variant['inputPlaceholder'] ?? ''],
      dropdownBtnText: [variant['dropdownBtnText'] ?? ''],
      dropdownBtnTextClose: [variant['dropdownBtnTextClose'] ?? ''],
      dropdownMessage: [variant['dropdownMessage'] ?? ''],
      addToCartEnabled: [variant['addToCartEnabled']],
      errorMessages: [variant['errorMessages'] ?? ''],
      waitMessages: [variant['waitMessages'] ?? ''],
      sampleMessages: this.fb.array(sampleMessages.map(message => this.createSampleMessageGroup(message)))
    });
  }

  private ensureSupportedLocalizationVariantForms(defaultLocale: string, supportedLocales: string[]): void {
    const normalizedDefaultLocale = defaultLocale.toLowerCase();
    const existingLocales = new Set(
      this.localizationVariantsForm.controls
        .map(control => (control.value.locale ?? '').toString().trim().toLowerCase())
        .filter(locale => !!locale)
    );

    supportedLocales
      .map(locale => (locale || '').trim())
      .filter(locale => !!locale && locale.toLowerCase() !== normalizedDefaultLocale)
      .forEach(locale => {
        const normalizedLocale = locale.toLowerCase();
        if (existingLocales.has(normalizedLocale)) return;

        this.localizationVariantsForm.push(this.createLocalizationVariantGroup(locale));
        existingLocales.add(normalizedLocale);
      });
  }

  private buildLocalizationVariantsPayload(): Record<string, any> {
    const variants: Record<string, any> = {};
    this.localizationVariantsForm.controls.forEach(control => {
      const value = control.value;
      const locale = (value.locale ?? '').toString().trim();
      if (!locale) return;

      variants[locale] = {
        generatedFromHash: value.generatedFromHash ?? '',
        generatedAt: value.generatedAt || null,
        widgetOptions: {
          text: {
            aiMessage: value.widgetText ?? ''
          }
        },
        frontEndOptions: {
          introMessage: value.introMessage ?? '',
          firstMessage: value.firstMessage ?? '',
          inputPlaceholder: value.inputPlaceholder ?? '',
          dropdownBtnText: value.dropdownBtnText ?? '',
          dropdownBtnTextClose: value.dropdownBtnTextClose ?? '',
          dropdownMessage: value.dropdownMessage ?? '',
          ...(value.addToCartEnabled === null || value.addToCartEnabled === undefined ? {} : { addToCartEnabled: value.addToCartEnabled === true }),
          errorMessages: this.linesToTemplateMessages(value.errorMessages),
          waitMessages: this.linesToTemplateMessages(value.waitMessages),
          sampleMessages: this.normalizeSampleMessages(value.sampleMessages)
        }
      };
    });
    return variants;
  }

  private linesToTemplateMessages(value: string): ITemplateMessage[] {
    return (value || '')
      .split('\n')
      .map(message => message.trim())
      .filter(message => !!message)
      .map(message => ({ message }));
  }

  private resolveFontProvider(styling?: IStyling): string {
    if (styling?.fontProvider) return styling.fontProvider;
    return styling?.fontPreset || styling?.fontFaces?.length ? 'custom' : 'google';
  }

  private getFontFaceUrl(styling: IStyling | undefined, weight: number): string {
    const face = styling?.fontFaces?.find(item => Number(item.weight ?? 400) === weight && (item.style ?? 'normal') === 'normal');
    return face?.url ?? '';
  }

  private normalizeStylingForSave(styling: IStyling & Record<string, any>): IStyling {
    const provider = (styling.fontProvider || 'google').toString().trim().toLowerCase();
    const normalized = { ...styling } as IStyling & Record<string, any>;

    normalized.fontProvider = provider;
    normalized.fontFamily = (normalized.fontFamily || '').toString().trim();
    normalized.fontFallback = (normalized.fontFallback || 'Inter, Arial, sans-serif').toString().trim();
    normalized.fontPreset = provider === 'custom' ? (normalized.fontPreset || '').toString().trim() : '';
    normalized.fontFaces = provider === 'custom' && !normalized.fontPreset
      ? [
        this.buildFontFace(normalized['fontFaceRegularUrl'], 400),
        this.buildFontFace(normalized['fontFaceMediumUrl'], 500),
        this.buildFontFace(normalized['fontFaceSemiBoldUrl'], 600),
        this.buildFontFace(normalized['fontFaceBoldUrl'], 700)
      ].filter((face): face is IFontFace => !!face)
      : [];

    if (provider === 'google') {
      normalized.fontFamily = '';
    }
    if (provider !== 'custom') {
      normalized.fontPreset = '';
      normalized.fontFaces = [];
    }

    delete normalized['fontFaceRegularUrl'];
    delete normalized['fontFaceMediumUrl'];
    delete normalized['fontFaceSemiBoldUrl'];
    delete normalized['fontFaceBoldUrl'];

    return normalized as IStyling;
  }

  private buildFontFace(url: string | undefined, weight: number): IFontFace | null {
    const trimmedUrl = (url || '').toString().trim();
    if (!trimmedUrl) return null;

    return {
      url: trimmedUrl,
      weight: String(weight),
      style: 'normal',
      format: this.inferFontFormat(trimmedUrl)
    };
  }

  private inferFontFormat(url: string): string {
    const lower = url.toLowerCase();
    if (lower.includes('.woff2')) return 'woff2';
    if (lower.includes('.woff')) return 'woff';
    if (lower.includes('.otf')) return 'opentype';
    return 'truetype';
  }

  private normalizeSampleMessages(messages: ISampleMessage[]): ISampleMessage[] {
    return (messages || [])
      .map(message => ({
        headline: this.cleanSampleMessageText(message.headline),
        description: this.cleanSampleMessageText(message.description),
        imageUrl: (message.imageUrl || '').trim(),
        imageAlt: this.cleanSampleMessageText(message.imageAlt),
        category: this.cleanSampleMessageText(message.category || message.imageAlt)
      }))
      .filter(message => !!message.headline && !!message.description);
  }

  private cleanSampleMessageText(value?: string): string {
    return cleanQuickStartText(value);
  }

  // Save Chatbot Settings
  save(includeHubSpotSync: boolean = true) {
    if (!this.chatbot) return;

    if (!this.ensurePersistableFormsValid()) return;

    const shouldSyncHubSpot = includeHubSpotSync && this.hubspotForm.dirty;
    if (!shouldSyncHubSpot) {
      this.persistChatbotSettings(false);
      return;
    }

    this.hubspotForm.markAllAsTouched();
    if (this.hubspotForm.invalid) {
      this.hubspotSyncError = 'Select a HubSpot inbox before enabling sync.';
      this.snackBar.showError(this.hubspotSyncError, 3000);
      return;
    }

    this.hubspotSyncError = '';
    const payload = this.buildHubSpotSyncPayload();
    (this.chatbot as any).hubSpotSync = {
      ...(this.chatbot as any).hubSpotSync,
      ...payload
    };
    this.persistChatbotSettings(true);
  }

  // Delete Chatbot
  delete() {
    if (!this.chatbot) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this?',
        message: 'This will delete the record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.snackBar.showInfo('Deleting chatbot...', 3000);
        this.chatbotsService.deleteAsync(this.chatbot?.id ?? "").subscribe(() => {
          this.snackBar.showSuccess('Chatbot deleted successfully.', 3000);
          this.router.navigate(['/ai/chatbots']);
        }, error => {
          console.error('Error deleting chatbot', error);
          this.snackBar.showError('Error deleting chatbot.', 3000);
          this.isDeleting = false;
        });
      } else {
        this.snackBar.showInfo('Delete action canceled.', 3000);
      }
    });
  }

  // Propose Initial Instruction
  proposeInitialInstruction() {
    if (!this.chatbot) return;
    this.isProposing = true;
    this.snackBar.showInfo('Generating initial instructions...', 3000);

    this.save(false); // Save current state first (skip HubSpot sync)

    setTimeout(async () => {
      try {
        const resp = await this.chatbotsService.proposeInitialInstructionForChatbotAsync(this.chatbot?.id ?? "").toPromise() as any;
        if (!resp.value || !resp.value['Instructions'] || !this.chatbot) {
          this.snackBar.showError('There was an error getting your proposed instructions', 3000);
          return;
        }
        this.chatbot.gptClassificationAndSanitizeQuery = resp.value['Instructions'];
        this.settingsForm.patchValue({
          gptClassificationAndSanitizeQuery: this.chatbot.gptClassificationAndSanitizeQuery
        });
        this.snackBar.showSuccess('Initial instructions updated', 3000);
      } catch (error) {
        console.error('Error proposing initial instruction', error);
        this.snackBar.showError('An error occurred, see console for more information', 3000);
      } finally {
        this.isProposing = false;
      }
    }, 5000);
  }

  private normalizeLayoutPreset(value: unknown): string {
    const normalized = `${value ?? ''}`
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
    return normalized === 'active-brands' ? 'compact-mobile' : normalized;
  }

  private normalizeHeaderDisplayMode(value: unknown): string {
    const normalized = `${value ?? ''}`
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
    return normalized === 'logo-only' || normalized === 'logo' ? 'logo-only' : '';
  }

  private normalizeLauncherShape(value: unknown): string {
    return `${value ?? ''}`.trim().toLowerCase() === 'rectangle' ? 'rectangle' : 'circle';
  }

  private normalizeSampleMessagesClickAnimationDelay(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 140;
    }
    return Math.min(parsed, 600);
  }

  private normalizeProductCardPresentation(value: unknown): string {
    const normalized = `${value ?? ''}`
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
    const compatible = normalized === 'active-brands-carousel' || normalized === 'active-brands'
      ? 'compact-carousel'
      : normalized;
    return /^(horizontal|horizontal-compact|vertical|vertical-list|vertical-carousel|compact-carousel)$/.test(compatible)
      ? compatible
      : 'horizontal';
  }

  private normalizeProductCardPresentationMode(value: unknown): 'fixed' | 'dynamic' {
    return `${value ?? ''}`.trim().toLowerCase() === 'dynamic' ? 'dynamic' : 'fixed';
  }

  private normalizeProductCardDynamicPresentations(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return [...new Set(value
      .map(entry => `${entry ?? ''}`.trim().replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/[\s_]+/g, '-').toLowerCase())
      .map(entry => entry === 'active-brands' || entry === 'active-brands-carousel' ? 'compact-carousel' : entry)
      .filter(entry => /^(horizontal|horizontal-compact|vertical|vertical-list|vertical-carousel|compact-carousel)$/.test(entry)))];
  }

}
